<template>
  <div class="ai-assistant-container">
    <div class="row h-100">
      <!-- Chat List Sidebar -->
      <div class="chat-sidebar" :class="{ 'admin-view-active': adminHistory }">
        <div class="sidebar-header">
          <h3 class="mb-0">PAICE</h3>
          <div class="d-flex justify-content-end align-items-center gap-2">
            <div v-if="$store.getters.userRoles.isAqferAdmin || $store.getters.userRoles.isAqferManager"
              class="history-scope-toggle" role="group" aria-label="Chat history scope">
              <button type="button" class="scope-option" :class="{ active: !adminHistory }" title="Show only your conversations"
                @click="adminHistory && toggleAdminHistory()">
                <i class="fa-solid fa-user"></i>
                <span>Mine</span>
              </button>
              <button type="button" class="scope-option" :class="{ active: adminHistory }" title="Show every user's conversations"
                @click="!adminHistory && toggleAdminHistory()">
                <i class="fa-solid fa-users"></i>
                <span>All users</span>
              </button>
            </div>
            <button class="btn btn-primary btn-sm" title="New chat" @click="createNewChat()">
              <i class="fa fa-plus"></i>
            </button>
          </div>
        </div>
        <!-- <div v-if="adminHistory" class="admin-view-banner">
          <i class="fa-solid fa-shield-halved"></i>
          <span>Viewing chat history for all users</span>
        </div> -->
        <div class="sidebar-list-controls">
          <span class="chat-count-pill">
            <i class="fa-regular fa-comments me-1"></i>
            <strong>{{ chats.length }}</strong>
            <span>conversation{{ chats.length !== 1 ? 's' : '' }}</span>
          </span>
          <div class="list-controls-actions">
            <label class="limit-field" title="Maximum conversations to fetch">
              <span class="limit-field-label">Show</span>
              <input type="number" v-model.number="chatLimit" @change="loadChats(true)" class="limit-field-input"
                min="1" max="500" />
            </label>
            <button class="list-control-btn" title="Refresh conversations" :disabled="loadingChats" @click="loadChats(true)">
              <i :class="['fa', loadingChats ? 'fa-spinner fa-spin' : 'fa-rotate-right']"></i>
            </button>
          </div>
        </div>
        <div class="chat-list">
          <!-- Loading state for chat list -->
          <div v-if="loadingChats" class="loading-container">
            <i class="fa fa-spinner fa-spin fa-2x"></i>
            <p>Loading conversations...</p>
          </div>

          <!-- Chat list -->
          <template v-else>
            <div v-for="chat in paginatedChats" :key="chat.resourceId" class="chat-item"
              :class="{ active: currentChat?.resourceId === chat.resourceId, otherUserChat: !isChatMine(chat) }" @click="selectChat(chat)">
              <div class="chat-info">
                <div class="chat-title">{{ chat.title || 'New Chat' }}</div>
                <div class="chat-date">
                  {{ formatDate(chat.updatedAt) }}
                  <span v-if="!isChatMine(chat)" class="badge bg-secondary chat-user">{{ chat.userId }}</span>
                </div>
              </div>
              <button v-if="$store.getters.userRoles.isAqferAdmin || ($store.getters.userRoles.isAccessByRole(deleteChatRoles) && isChatMine(chat))"
                class="delete-chat-btn" @click.stop="deleteChat(chat)" title="Delete conversation">
                <i class="fa fa-trash"></i>
              </button>
            </div>
            <div v-if="chats.length === 0" class="no-chats">
              No conversations yet. Click "New Chat" to start!
            </div>
          </template>
        </div>

        <!-- Pagination — a sibling of the scroll container, so it stays pinned
             to the bottom of the sidebar while only the chat items scroll. -->
        <div v-if="!loadingChats && chats.length > 0 && totalPages > 1" class="chat-pagination">
          <div class="pagination-info">
            Showing {{ startIndex + 1 + "-" + endIndex }} of {{ chats.length }}
          </div>
          <nav aria-label="Chat pagination">
            <ul class="pagination pagination-sm mb-0">
              <li class="page-item" :class="{ disabled: currentPage === 1 }">
                <a class="page-link" href="#" @click.prevent="goToPage(currentPage - 1)" aria-label="Previous">
                  <i class="fa fa-chevron-left"></i>
                </a>
              </li>
              <template v-for="(item, index) in visiblePages" :key="index">
                <li v-if="item === 'ellipsis'" class="page-item disabled">
                  <span class="page-link">...</span>
                </li>
                <li v-else class="page-item" :class="{ active: currentPage === item }">
                  <a class="page-link" href="#" @click.prevent="goToPage(item)">{{ item }}</a>
                </li>
              </template>
              <li class="page-item" :class="{ disabled: currentPage === totalPages }">
                <a class="page-link" href="#" @click.prevent="goToPage(currentPage + 1)" aria-label="Next">
                  <i class="fa fa-chevron-right"></i>
                </a>
              </li>
            </ul>
          </nav>
        </div>
      </div>

      <!-- Chat Area -->
      <div class="chat-area">
        <div v-if="!currentChat" class="empty-state-container">
          <div class="empty-state-content">
            <img src="@/assets/paice.jpg" alt="PAICE" class="paice-logo" />
            <h2 class="mb-1">PAICE</h2>
            <h5 class="text-muted mb-4">Private AI Chat Environment</h5>
            <h1>How can I help you today?</h1>
          </div>

          <!-- Input Area for Empty State -->
          <div class="empty-input-area">
            <div class="input-wrapper">
              <!-- File attachments preview -->
              <div v-if="attachedFiles.length > 0" class="file-attachments-bar">
                <div v-for="(file, idx) in attachedFiles" :key="idx" class="file-chip" :title="file.name">
                  <i :class="[fileTypeIcon[file.name.split('.').pop()] || 'fa fa-file-alt', 'file-chip-icon']"></i>
                  <span class="file-chip-name">{{ file.name }}</span>
                  <button class="file-chip-remove" @click="removeAttachedFile(idx)" :disabled="isStreaming">
                    <i class="fa fa-times"></i>
                  </button>
                </div>
              </div>
              <!-- Textarea -->
              <textarea v-model="userInput" @keydown.enter="handleEnter"
                placeholder="Type your message here to start a new chat..." :disabled="isStreaming" rows="1"
                class="form-control input-textarea" ref="emptyMessageInput"></textarea>
              <!-- Toolbar -->
              <div class="input-toolbar">
                <div class="toolbar-actions gap-2">
                  <button class="btn btn-sm toolbar-btn" @click="triggerFileSelect" :disabled="isStreaming">
                    <i class="fa fa-paperclip"></i>
                    <span>Attach</span>
                  </button>
                  <div class="btn btn-sm model-id" v-if="paiceSettings?.modelId">
                    <i class="fa fa-brain"></i>
                    <span> {{ paiceSettings.modelId }} </span>
                  </div>
                </div>
                <div class="d-flex align-items-center gap-2">
                  <span class="input-hint text-muted">Shift+Enter for new line</span>
                  <button
                    class="btn send-button"
                    :class="isStreaming ? 'btn-danger' : 'btn-primary'"
                    @click="isStreaming ? abortStream() : sendMessage()"
                    :disabled="aborting || (!isStreaming && (!userInput.trim() && attachedFiles.length === 0))"
                    :title="isStreaming ? 'Stop generating' : 'Send message'"
                  >
                    <i :class="isStreaming ? 'fa fa-stop' : 'fa fa-paper-plane'"></i>
                  </button>
                </div>
              </div>
            </div>
            <div class="ai-disclaimer">This tool uses AI, which can make mistakes. Verify important outputs before acting on them.</div>
          </div>
        </div>

        <div v-else class="chat-container">
          <!-- Messages -->
          <div class="messages-container" ref="messagesContainer" @scroll="handleMessagesScroll">
            <!-- Loading state for messages -->
            <div v-if="loadingMessages" class="loading-container">
              <i class="fa fa-spinner fa-spin fa-2x"></i>
              <p>Loading messages...</p>
            </div>

            <!-- Messages list -->
            <template v-else>
              <div v-for="(message, index) in messages" :key="message.clientId || message.messageId || index" class="message" :class="message.role">
                <div class="message-avatar">
                  <i v-if="message.role === 'user'" class="fa fa-user"></i>
                  <img v-else src="@/assets/paice.jpg" alt="PAICE" class="avatar-image" />
                </div>
                <div class="message-content">
                  <!-- Attached files display for user messages -->
                  <div v-if="message.role === 'user' && getMessageFiles(message).length > 0" class="message-files">
                    <div
                      v-for="(file, fidx) in getMessageFiles(message)"
                      :key="fidx"
                      class="message-file-chip"
                      @click="viewFileContent(message.resourceId, message.messageId, file)"
                      title="Click to view file"
                    >
                      <i :class="[fileTypeIcon[file.name.split('.').pop()] || 'fa fa-file-alt']"></i>
                      <span class="message-file-name">{{ file.name }}</span>
                    </div>
                    <div
                      v-if="getMessageFiles(message).length > 1"
                      :key="fidx"
                      class="message-file-chip-view-all"
                      @click="viewFileContent(message.resourceId, message.messageId)"
                      title="Click to view all files"
                    >
                      <span class="message-file-name">View all</span>
                    </div>
                  </div>
                  <!-- Agent status accordion (handles both streaming and saved messages) -->
                  <!-- Each message uses its own stable id in the collapse target -->
                  <div v-if="message.role === 'assistant' && (message.statusUpdates || message.streaming)" class="mb-2">
                    <div class="agent-status collapsed" role="button" data-bs-toggle="collapse" :data-bs-target="`#collapse-${message.clientId || message.messageId}`"
                      :aria-controls="`collapse-${message.clientId || message.messageId}`" aria-expanded="false">
                      <i v-if="message.streaming" class="fa-solid fa-circle fa-beat-fade text-success fs-5"></i>
                      <i v-else :class="['fa-solid', message.isGuardrailIntervened == true ? 'fa-xmark text-danger slight-margin-down' : 'fa-check text-success']"></i>
                      <span class="status-text text-muted fst-italic fw-bold">
                        {{ message.streaming ? getCurrentAgentStatus : getAgentStatus(message) }}
                      </span>
                      <i v-if="(message.statusUpdates || []).length > 0" class="fa-solid fa-angle-down text-muted collapse-arrow-down" style="font-size: 0.75rem; margin-top: 0.15rem;"></i>
                      <i v-if="(message.statusUpdates || []).length > 0" class="fa-solid fa-angle-up text-muted collapse-arrow-up" style="font-size: 0.75rem; margin-top: 0.15rem;"></i>
                    </div>
                    <!-- Unique id per message — no collisions between messages -->
                    <div v-if="(message.statusUpdates || []).length > 0" class="status-text text-muted mt-2 collapse" :id="`collapse-${message.clientId || message.messageId}`">
                      <div class="status-list">
                        <div v-for="(status, sIdx) in message.statusUpdates" :key="sIdx" class="status-row row py-2 mx-0 border-bottom">
                          <div class="col-auto px-0 pt-1 pe-2 d-flex align-items-start flex-shrink-0 d-flex justify-content-center" style="width: 1.5rem;">
                            <i :class="getAgentStatusIcon(status.label, streaming = (message.streaming && currentStatus(message.statusUpdates)?.[sIdx] == true))"></i>
                          </div>
                          <!-- Label: fixed width, wraps if needed -->
                          <div class="col-2 px-0 pe-2 fw-bold flex-shrink-0" style="min-width: 8rem; max-width: 8rem; word-break: break-word;">
                            {{ status.label }}
                          </div>
                          <!-- Detail: takes remaining space -->
                          <div class="col px-0">
                            <span>{{ status.detail }}</span>
                          </div>
                          <!-- Timestamp: fixed width, right-aligned -->
                          <div class="col-auto text-end px-0 flex-shrink-0" style="min-width: 10rem;">
                            <span>{{ (status.timestamp || '').replace(/T/, ' ').replace(/Z/, '') }}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div v-if="getMessageText(message) || (message.uiResources && message.uiResources.length > 0)" :class="['message-text', message.isGuardrailIntervened == true ? 'blocked-message-text' : '']">
                    <div v-html="renderMarkdown(getMessageText(message), message)"></div>
                    <!-- UI Resource renderer - shared between streaming and saved messages.
                         Stable key (message id + resource uri) keeps the SAME McpAppRenderer
                         instance alive across the streaming -> saved transition, so the iframe
                         is never torn down and re-mounted (no reload). -->
                    <div v-if="message.uiResources && message.uiResources.length > 0" class="ui-resource-inline mt-3">
                      <div v-for="(resource, idx) in message.uiResources" :key="`${message.clientId || message.messageId}-${resource.uri}-${idx}`"
                        class="ui-resource-card">
                        <McpAppRenderer :resource-uri="resource.uri" :tool-name="resource.toolName"
                          :server-id="toolServerIdMap[resource.toolName] || null"
                          :tool-input="resource.toolInput" :tool-result="resource.toolResult" :surface-url="agentUrl"
                          :access-token="accessToken" :cid="currentClient.cid"
                          :resource-id="currentChat?.resourceId"
                          :theme="$store.state.darkMode ? 'dark' : 'light'" />
                      </div>
                    </div>
                  </div>
                  <!-- Only the prompts still holding the turn up. Below the text
                       so the card sits where the turn actually stopped, and above
                       the typing indicator so the question is the last thing read.
                       Once answered, the card goes and the answer shows as a chip
                       on the tool-call row it belongs to, so nothing piles up
                       under a finished turn. -->
                  <div v-if="openToolPermissions(message).length > 0" class="tool-perm-list">
                    <McpToolPermissionPrompt
                      v-for="record in openToolPermissions(message)"
                      :key="record.toolUseId"
                      :record="record"
                      :submit="onToolPermissionDecide" />
                  </div>
                  <!-- Streaming typing indicator (only while this message is streaming) -->
                  <div v-if="message.streaming" class="streaming-indicator">
                    <span class="dot"></span>
                    <span class="dot"></span>
                    <span class="dot"></span>
                  </div>
                  <!-- When response is cancelled, show a notice with option to retry -->
                  <div v-if="message.role == 'assistant' && message.isResponseCancelled" class="text-danger fs-5 align-items-center d-flex mt-1">
                    <i class="fa-solid fa-ban me-2"></i>
                    Response stopped.
                    <button v-if="isLastAssistantMessage(index) && !isStreaming" @click="streamResponse()" class="px-1 text-danger btn btn-sm btn-link fw-bold">
                      Click to retry
                    </button>
                  </div>
                  <!-- Feedback (thumbs + optional comment) on completed assistant messages -->
                  <div
                    v-if="message.role === 'assistant' && message.messageId && !message.isResponseCancelled"
                    class="message-feedback"
                    :class="{ 'has-feedback': !!getFeedbackRating(message) }"
                  >
                    <span class="feedback-label" v-if="isChatMine(currentChat)">Rate this response</span>
                    <button
                      class="feedback-btn feedback-button-thumbs-up"
                      :class="{ active: getFeedbackRating(message) === 'thumbs_up', disabled: !isChatMine(currentChat), 'just-rated': animatingFeedback[`${message.messageId}_thumbs_up`] }"
                      title="Helpful"
                      @click="toggleRating(message, 'thumbs_up')"
                      :disabled="!isChatMine(currentChat)"
                    >
                      <i :class="[getFeedbackRating(message) === 'thumbs_up' ? 'fa' : 'fa-regular', 'fa-thumbs-up']"></i>
                    </button>
                    <button
                      class="feedback-btn feedback-button-thumbs-down" style="margin-top: 0.165rem;"
                      :class="{ active: getFeedbackRating(message) === 'thumbs_down', disabled: !isChatMine(currentChat), 'just-rated': animatingFeedback[`${message.messageId}_thumbs_down`] }"
                      title="Not helpful"
                      @click="toggleRating(message, 'thumbs_down')"
                      :disabled="!isChatMine(currentChat)"
                    >
                      <i :class="[getFeedbackRating(message) === 'thumbs_down' ? 'fa' : 'fa-regular', 'fa-thumbs-down']"></i>
                    </button>
                    <button
                      v-if="getFeedbackRating(message)"
                      class="feedback-btn feedback-button-comment" style="margin-top: 0.1rem;"
                      :class="{ active: !!message.feedback?.comment, disabled: !isChatMine(currentChat) }"
                      :title="isChatMine(currentChat) ? (message.feedback?.comment ? 'Edit comment' : 'Add comment') : 'View comment'"
                      @click="openCommentBox(message)"
                      :disabled="!isChatMine(currentChat) && !message.feedback?.comment"
                    >
                      <i :class="[!!message.feedback?.comment ? 'fa' : 'fa-regular', 'fa-comment-dots']"></i>
                    </button>
                    <div v-if="commentingMessageId === message.messageId" class="feedback-popover">
                      <textarea
                        v-model="commentDraft"
                        class="form-control form-control-sm"
                        rows="3"
                        placeholder="What worked or didn't?"
                        ref="feedbackTextarea"
                        :disabled="!isChatMine(currentChat)"
                      ></textarea>
                      <div class="d-flex justify-content-end gap-2 mt-2">
                        <button class="btn btn-sm btn-light" @click="closeCommentBox()">{{ isChatMine(currentChat) ? 'Cancel' : 'Close' }}</button>
                        <button class="btn btn-sm btn-primary" @click="submitComment(message)" v-if="isChatMine(currentChat)">Save</button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </template>
          </div>

          <transition name="scroll-btn-fade">
            <button
              v-if="showScrollButton"
              class="scroll-to-bottom-btn"
              @click="scrollToBottom"
              title="Scroll to bottom"
            >
              <i class="fa fa-chevron-down"></i>
              <span>Scroll To bottom</span>
            </button>
          </transition>

          <!-- Input Area -->
          <div class="input-area">
            <div class="input-wrapper">
              <!-- File attachments preview -->
              <div v-if="attachedFiles.length > 0" class="file-attachments-bar">
                <div v-for="(file, idx) in attachedFiles" :key="idx" class="file-chip" :title="file.name">
                  <i :class="[fileTypeIcon[file.name.split('.').pop()] || 'fa fa-file-alt', 'file-chip-icon']"></i>
                  <span class="file-chip-name">{{ file.name }}</span>
                  <button class="file-chip-remove" @click="removeAttachedFile(idx)" :disabled="isStreaming">
                    <i class="fa fa-times"></i>
                  </button>
                </div>
              </div>
              <!-- Textarea -->
              <textarea v-model="userInput" @keydown.enter="handleEnter" placeholder="Type your message here..."
                :disabled="!isChatMine(currentChat) || isStreaming" rows="1" class="form-control input-textarea" ref="messageInput"></textarea>
              <!-- Toolbar -->
              <div class="input-toolbar">
                <div class="toolbar-actions gap-2">
                  <button class="btn btn-sm toolbar-btn" @click="triggerFileSelect"
                    :disabled="!isChatMine(currentChat) || isStreaming">
                    <i class="fa fa-paperclip"></i>
                    <span>Attach</span>
                  </button>
                  <div class="btn btn-sm model-id" v-if="paiceSettings?.modelId">
                    <i class="fa fa-brain"></i>
                    <span> {{ paiceSettings.modelId }} </span>
                  </div>
                </div>
                <div class="d-flex align-items-center gap-2">
                  <span class="input-hint text-muted">Shift+Enter for new line</span>
                  <button
                    class="btn send-button"
                    :class="isStreaming ? 'btn-danger' : 'btn-primary'"
                    @click="isStreaming ? abortStream() : sendMessage()"
                    :disabled="aborting || (!isStreaming && (!userInput.trim() && attachedFiles.length === 0))"
                    :title="isStreaming ? 'Stop generating' : 'Send message'"
                  >
                    <i :class="isStreaming ? 'fa fa-stop' : 'fa fa-paper-plane'"></i>
                  </button>
                </div>
              </div>
            </div>
            <div class="ai-disclaimer">This tool uses AI, which can make mistakes. Verify important outputs before acting on them.</div>
          </div>
        </div>
      </div>
    </div>

    <!-- Hidden file input (shared) -->
    <input type="file" ref="fileInput" multiple @change="handleFileSelect" class="d-none"
      accept=".txt,.csv,.json,.yaml,.yml,.md,.sql,.py,.vue,.js,.ts,.go,.java,.sh,.log,.html,.css,.scss,.avro,.parquet" />

    <!-- Visualization Flyout Panel -->
    <transition name="flyout" @after-enter="onFlyoutEnter" @before-leave="onFlyoutLeave">
      <div v-if="flyoutResource" class="visualization-flyout">
        <div class="flyout-header">
          <h5 class="flyout-title">
            <i class="fa fa-chart-bar me-2"></i>
            {{ getVisualizationLabel(flyoutResource.toolName) }}
          </h5>
          <button class="btn btn-sm btn-close-flyout" @click="closeFlyout" title="Close">
            <i class="fa fa-times"></i>
          </button>
        </div>
        <div class="flyout-content">
          <McpAppRenderer v-if="flyoutReady" :key="flyoutResource.uri" ref="flyoutRenderer"
            :resource-uri="flyoutResource.uri" :tool-name="flyoutResource.toolName"
            :server-id="toolServerIdMap[flyoutResource.toolName] || null"
            :tool-input="flyoutResource.toolInput" :tool-result="flyoutResource.toolResult" :surface-url="agentUrl"
            :access-token="accessToken" :cid="currentClient.cid"
            :resource-id="currentChat?.resourceId" :theme="$store.state.darkMode ? 'dark' : 'light'"/>
        </div>
      </div>
    </transition>

    <!-- Flyout backdrop -->
    <div v-if="flyoutResource" class="flyout-backdrop" @click="closeFlyout"></div>
  </div>
</template>

<script>
import { marked } from 'marked';
import hljs from 'highlight.js/lib/core';
// Import only the languages you need to reduce bundle size
import javascript from 'highlight.js/lib/languages/javascript';
import python from 'highlight.js/lib/languages/python';
import json from 'highlight.js/lib/languages/json';
import bash from 'highlight.js/lib/languages/bash';
import sql from 'highlight.js/lib/languages/sql';
import yaml from 'highlight.js/lib/languages/yaml';
import xml from 'highlight.js/lib/languages/xml';
import 'highlight.js/styles/github-dark.css';
import axios from 'axios';
import { v4 as uuidv4 } from 'uuid';
import { mapActions, mapState } from 'vuex';
import McpAppRenderer from '@/components/McpAppRenderer.vue';
import McpToolPermissionPrompt from '@/components/paice/McpToolPermissionPrompt.vue';
import { fetchMcpTools, getToolUiResourceUri } from '@/services/surfaceApiMcpAdapter';
import { formatBytes, getAgentStatusIcon, currentStatus } from '../../utils/commonFunction';
import { getAgentsRequestQueryParams, getAgentsPayloadParams } from '@/utils/agentsApi';
import { registerAgentStream, releaseAgentStream } from '@/utils/agentStreamGuard';
import {
  NO_SETTINGS_STORED, settingsVersionToken, settingsPreconditionHeaders,
} from '@/store/modules/paice.store';

// Register languages
hljs.registerLanguage('javascript', javascript);
hljs.registerLanguage('python', python);
hljs.registerLanguage('json', json);
hljs.registerLanguage('bash', bash);
hljs.registerLanguage('sql', sql);
hljs.registerLanguage('yaml', yaml);
hljs.registerLanguage('xml', xml);

let activeMcpTools = [];

// Pail tools that leave a downloadable result file behind. Mirrors
// QUERY_RESULT_TOOLS in the agents service — the stream only emits artifacts for
// these, so a fence for any other tool never gets a download control.
const QUERY_RESULT_TOOLS = new Set([
  'execute_athena_query',
  'get_query_results',
  'show_query_visualization',
]);

// Query artifacts for the message marked() is currently rendering. renderer.code
// is stateless and gets no message context, so renderMarkdown() stages them here
// and each matching tool fence claims one, in document order.
let activeQueryArtifacts = [];

// Permission records for the message marked() is currently rendering, staged the
// same way as artifacts: renderer.code gets no message context, so each tool
// fence claims the record for its own call.
let activeToolPermissions = [];

function escapeAttr(value) {
  return String(value == null ? '' : value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}

// Split a fence info string into the tool name and, for query tools, the
// toolUseId the agents service appends as `tool_name#toolUseId`.
function parseFenceInfo(info) {
  const raw = info || '';
  const hash = raw.indexOf('#');
  if (hash === -1) return { toolName: raw, toolUseId: '' };
  return { toolName: raw.slice(0, hash), toolUseId: raw.slice(hash + 1) };
}

// Claim the artifact belonging to this tool fence, if any.
//
// Preferred match is by toolUseId, which the fence carries for query tools.
// Order cannot be trusted: fences stream in the order the agent called the
// tools, but artifacts are emitted as tool results land, and parallel calls
// finish in whatever order the queries complete — so the Nth fence is not
// reliably the Nth artifact.
function claimQueryArtifact(toolName, toolUseId) {
  if (!QUERY_RESULT_TOOLS.has(toolName)) return null;
  const artifact = toolUseId
    ? activeQueryArtifacts.find(a => !a.claimed && a.toolUseId === toolUseId)
    : activeQueryArtifacts.find(a => !a.claimed && a.toolName === toolName);
  if (!artifact) return null;
  artifact.claimed = true;
  return artifact;
}

/**
 * The permission record belonging to this tool fence, if the user was asked.
 *
 * toolUseId is authoritative when the fence carries one: a turn that calls the
 * same tool twice gets one answer per call, and matching on the name would put
 * the first call's answer on the second call's row. Only fences without an id
 * fall back to the name, in document order. Claimed either way, so two rows
 * never show the same answer.
 */
function claimToolPermission(toolName, toolUseId) {
  const pool = activeToolPermissions.filter(p => !p.claimed);
  if (!pool.length) return null;
  const record = toolUseId
    ? pool.find(p => p.toolUseId === toolUseId)
    : pool.find(p => p.toolName === toolName || p.rawToolName === toolName);
  if (!record) return null;
  record.claimed = true;
  return record;
}

/**
 * What the user clicked, as a chip on the row. The outcomes with no decision
 * behind them are the ones nobody answered.
 *
 * `always_allow` / `always_deny` are no longer offered — an answer covers one
 * call only — but chats saved while they were still can carry them, so they keep
 * their labels rather than falling through to the generic outcome chip.
 */

const PERMISSION_LABELS = {
  allowed: { text: 'Allowed', icon: 'fa-circle-check', tone: 'allowed' },
  denied: { text: 'Denied', icon: 'fa-ban', tone: 'denied' },
  timed_out: { text: 'Timed out', icon: 'fa-clock', tone: 'stale' },
  not_prompted: { text: 'Not run', icon: 'fa-circle-exclamation', tone: 'denied' },
};

function permissionChip(record) {
  if (!record) return '';
  const label = PERMISSION_LABELS[record.outcome]
    // No decision and no outcome: the prompt was still open when the stream
    // ended, so nothing ran and nothing will.
    || { text: 'Not answered', icon: 'fa-hand', tone: 'stale' };
  return `<span class="tool-perm-chip tool-perm-chip-${label.tone}"
      title="${escapeAttr(`${label.text} — ${record.serverName || 'external server'}`)}">
      <i class="fa-solid ${label.icon}" aria-hidden="true"></i>
      <span style="margin-top: 0.1rem">${escapeAttr(label.text)}</span>
    </span>`;
}

/**
 * The export control under a finished query. The link is not minted here — it
 * is presigned and short-lived, so it is fetched on click via
 * get_query_result_link.
 */
function downloadBar(artifact) {
  if (!artifact) return '';
  return `<div class="download-results-bar">
        <button class="btn btn-link btn-sm download-results"
          data-cid="${escapeAttr(artifact.cid)}"
          data-qid="${escapeAttr(artifact.queryExecutionId)}"
          title="Export the full query result as CSV">
          <i class="fa fa-download me-1"></i>Export results as CSV
        </button>
      </div>`;
}

// ── Tool calls ───────────────────────────────────────────────────────────────
// The agents service emits a tool call as a fenced block whose info string is
// the tool name, so a turn that used three tools arrives as three adjacent
// fences. Rendered as code blocks they outweigh the answer they were made to
// support, so a run of them is collapsed into one strip: a line per call naming
// what it touched, with the raw input a click away.

/** Groups and rows the user has opened, keyed per message so a re-render keeps them open. */
const openToolGroups = new Set();
const openToolRows = new Set();

/**
 * Fence infos that name a language rather than a tool.
 *
 * Only seven languages are registered with highlight.js above, so asking it is
 * not enough — the rest are listed here. The test has to be "is this a
 * language", not "is this a known tool": tools from an external MCP server are
 * not in the catalog this page loads, and gating on it left every `zcr_demo_*`
 * call rendering as a raw code block beside the rows it belonged with.
 */
const NON_TOOL_FENCES = new Set([
  'text', 'plaintext', 'plain', 'txt', 'log', 'md', 'markdown', 'html', 'xhtml',
  'css', 'scss', 'sass', 'less', 'go', 'golang', 'java', 'rust', 'rs', 'c',
  'cpp', 'c++', 'csharp', 'cs', 'ruby', 'rb', 'php', 'swift', 'kotlin', 'kt',
  'scala', 'r', 'perl', 'lua', 'dart', 'elixir', 'erlang', 'haskell', 'clojure',
  'sh', 'shell', 'zsh', 'console', 'powershell', 'ps1', 'bat', 'cmd',
  'dockerfile', 'docker', 'makefile', 'nginx', 'apache', 'ini', 'toml',
  'properties', 'csv', 'tsv', 'diff', 'patch', 'http', 'graphql', 'gql',
  'proto', 'jsx', 'tsx', 'ts', 'typescript', 'js', 'node', 'jsonc', 'json5',
  'ndjson', 'vue', 'svelte', 'tex', 'latex', 'mermaid', 'regex',
]);

/** Tool names are identifiers; language tags with punctuation are not. */
const TOOL_NAME_RE = /^[a-z][a-z0-9]*(?:_[a-z0-9]+)*$/;

/**
 * Whether a fence is a tool call rather than a code sample. A name the catalog
 * knows is always a tool; a name that is a language never is — which is what
 * keeps the Save and Execute controls on SQL blocks.
 */
function isToolFence(name) {
  if (!name) return false;
  if (QUERY_RESULT_TOOLS.has(name)) return true;
  if (activeMcpTools.some(tool => tool.name === name)) return true;
  const key = name.toLowerCase();
  if (hljs.getLanguage(key) || NON_TOOL_FENCES.has(key)) return false;
  return TOOL_NAME_RE.test(name);
}

function isToolToken(token) {
  return token.type === 'code' && isToolFence(parseFenceInfo(token.lang).toolName);
}

function clip(value, max) {
  const str = String(value == null ? '' : value);
  return str.length > max ? `${str.slice(0, max - 1).trimEnd()}\u2026` : str;
}

/**
 * The one line that tells two calls to the same tool apart: its first scalar
 * arguments, in the order the service sent them. Without it a stack of rows
 * reads as "three calls to get_job_runs" and says nothing worth reading.
 */
function argPrecis(code) {
  const raw = (code || '').trim();
  if (!raw) return '';
  let input;
  try {
    input = JSON.parse(raw);
  } catch (e) {
    // Half-written mid-stream, or never JSON. The first line still says more
    // than an empty column would.
    return clip(raw.split('\n').map(line => line.trim()).find(Boolean) || '', 72);
  }
  if (input === null || typeof input !== 'object') return clip(String(input), 72);
  if (Array.isArray(input)) return clip(`${input.length} items`, 72);

  const parts = [];
  let width = 0;
  for (const [key, value] of Object.entries(input)) {
    if (value === null || typeof value === 'object') continue;
    const flat = String(value).replace(/\s+/g, ' ').trim();
    if (!flat) continue;
    const part = `${key}: ${clip(flat, 64)}`;
    parts.push(part);
    width += part.length;
    if (parts.length >= 2 || width > 48) break;
  }
  // Every argument was nested — name the keys, since the values will not fit.
  if (!parts.length) return clip(Object.keys(input).join(', '), 72);
  return parts.join(' \u00b7 ');
}

/** A tool's input, pretty-printed when it parses so the drawer is readable. */
function highlightToolInput(code) {
  const raw = code || '';
  try {
    const pretty = JSON.stringify(JSON.parse(raw), null, 2);
    return { html: hljs.highlight(pretty, { language: 'json' }).value, lang: 'json' };
  } catch (e) {
    return { html: escapeAttr(raw), lang: '' };
  }
}

/** One collapsed row and the drawer it opens. */
function renderToolRow(token, { messageKey, index, running }) {
  const { toolName, toolUseId } = parseFenceInfo(token.lang);
  const key = `${messageKey}::${index}::${toolName}`;
  const open = openToolRows.has(key);
  const precis = argPrecis(token.text);
  const { html, lang } = highlightToolInput(token.text);
  const artifact = claimQueryArtifact(toolName, toolUseId);
  const permission = claimToolPermission(toolName, toolUseId);

  return `<div class="tool-call${open ? ' open' : ''}${running ? ' running' : ''}"
      data-tool-key="${escapeAttr(key)}">
      <button type="button" class="tool-row" aria-expanded="${open ? 'true' : 'false'}">
        <i class="fa-solid fa-chevron-right tool-chevron" aria-hidden="true"></i>
        <span class="tool-name">${escapeAttr(toolName)}</span>
        <span class="tool-precis" title="${escapeAttr(precis)}">${escapeAttr(precis)}</span>
        ${permissionChip(permission)}
        ${artifact ? '<i class="fa-solid fa-file-arrow-down tool-flag" aria-hidden="true"></i>' : ''}
      </button>
      <div class="tool-drawer">
        <div class="tool-drawer-inner">
          <pre><code class="hljs${lang ? ` language-${lang}` : ''}">${html}</code></pre>
          ${downloadBar(artifact)}
        </div>
      </div>
    </div>`;
}

/**
 * Which tools a run used, for the closed header. Repeats are counted rather
 * than listed — a turn that polls a long-running query calls the same tool
 * twelve times, and twelve copies of the name says less than "\u00d712".
 */
function toolNameSummary(entries) {
  const counts = new Map();
  entries.forEach(({ token }) => {
    const name = parseFenceInfo(token.lang).toolName;
    counts.set(name, (counts.get(name) || 0) + 1);
  });
  const parts = [...counts].map(([name, n]) => (n > 1 ? `${name} \u00d7${n}` : name));
  if (parts.length <= 3) return parts.join(' \u00b7 ');
  return `${parts.slice(0, 2).join(' \u00b7 ')} \u00b7 +${parts.length - 2} more`;
}

/**
 * A run of consecutive tool calls, as one strip that starts closed.
 *
 * Closed is the only sensible default: the calls are how the answer was
 * reached, not the answer, and a turn that polls a query's status can make a
 * dozen of them. So the header carries what is worth knowing without opening
 * anything — how many, and which — and the calls themselves are one click away.
 */
function renderToolGroup(entries, { messageKey, groupIndex, runningToken }) {
  const key = `${messageKey}::group::${groupIndex}`;
  const open = openToolGroups.has(key);
  const active = entries.find(entry => entry.token === runningToken);

  const rows = entries.map(({ token, index }) => renderToolRow(token, {
    messageKey,
    index,
    running: token === runningToken,
  })).join('');

  const count = `${entries.length} ${entries.length === 1 ? 'tool' : 'tools'}`;
  const summary = active
    ? `<span class="tool-calls-active">Using ${
      escapeAttr(parseFenceInfo(active.token.lang).toolName)}\u2026</span>`
    : `<span class="tool-calls-names">${escapeAttr(toolNameSummary(entries))}</span>`;

  return `<section class="tool-calls${open ? ' open' : ''}${active ? ' running' : ''}"
      data-group-key="${escapeAttr(key)}" aria-label="Tools used">
      <button type="button" class="tool-calls-head" aria-expanded="${open ? 'true' : 'false'}">
        <i class="fa-solid fa-chevron-right tool-chevron" aria-hidden="true"></i>
        <span class="tool-calls-count">${count}</span>
        ${summary}
      </button>
      <div class="tool-calls-body">
        <div class="tool-calls-body-inner">${rows}</div>
      </div>
    </section>`;
}

/**
 * Render a message, collapsing runs of adjacent tool fences into strips and
 * handing everything else to marked untouched. Fences are separated by a
 * `space` token, which is absorbed into the run rather than breaking it.
 */
function renderGroupedMarkdown(tokens, messageKey, streaming) {
  // The fence still being written is the tail of the stream; once prose follows
  // it the call is over. Anything looser leaves a finished row pulsing.
  let runningToken = null;
  if (streaming) {
    for (let i = tokens.length - 1; i >= 0; i -= 1) {
      if (tokens[i].type === 'space') continue;
      if (isToolToken(tokens[i])) runningToken = tokens[i];
      break;
    }
  }

  let html = '';
  let prose = [];
  let group = [];
  let held = [];
  let toolIndex = 0;
  let groupIndex = 0;

  const flushProse = () => {
    if (!prose.length) return;
    const slice = prose;
    // Reference-style link definitions live on the lexer's array, not the
    // tokens, so a slice has to carry them or those links render as text.
    slice.links = tokens.links;
    html += marked.parser(slice);
    prose = [];
  };
  const flushGroup = () => {
    if (!group.length) return;
    html += renderToolGroup(group, { messageKey, groupIndex, runningToken });
    groupIndex += 1;
    group = [];
  };

  tokens.forEach((token) => {
    if (isToolToken(token)) {
      flushProse();
      held = [];
      group.push({ token, index: toolIndex });
      toolIndex += 1;
      return;
    }
    if (token.type === 'space' && group.length) {
      held.push(token);
      return;
    }
    flushGroup();
    if (held.length) {
      prose.push(...held);
      held = [];
    }
    prose.push(token);
  });
  flushGroup();
  if (held.length) prose.push(...held);
  flushProse();
  return html;
}

// Configure marked with custom renderer that includes tool name headers
const renderer = new marked.Renderer();

renderer.code = function (token) {
  // In newer versions of marked, the code renderer receives a token object
  // Extract the code text and language from the token
  const codeStr = token.text || '';
  // `lang` is the raw fence info string, which for query tools also carries the
  // toolUseId. Everything downstream — highlighting, the header, the CSS class —
  // uses `toolName` so the id never surfaces in the UI.
  const { toolName: lang, toolUseId } = parseFenceInfo(token.lang);

  // Highlight the code
  let highlighted;
  if (lang && hljs.getLanguage(lang)) {
    try {
      highlighted = hljs.highlight(codeStr, { language: lang }).value;
    } catch (err) {
      console.error('Highlight error:', err);
      highlighted = hljs.highlightAuto(codeStr).value;
    }
  } else {
    try {
      highlighted = hljs.highlightAuto(codeStr).value;
    } catch (err) {
      // Escape HTML manually
      highlighted = codeStr.replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#039;');
    }
  }

  const isSql = lang.toLowerCase() === 'sql';

  // Tool fences never reach here — renderGroupedMarkdown claims them first —
  // so this only fires if the tool catalog has not loaded yet.
  const artifact = claimQueryArtifact(lang, toolUseId);

  // If there's a language/tool name, add a header
  if (lang) {
    return `<div class="code-block-wrapper">
      <div class="code-block-header d-flex justify-content-between align-items-center">
        <span class="code-block-lang">${activeMcpTools.some(tool => tool.name === lang) ? 'MCP: ' + lang : lang}</span>
        <div>
          ${isSql ? `<div class="d-flex align-items-end gap-2">
            <button class="btn btn-sm btn-secondary save-sql" @click="saveQuery" data-code="${encodeURIComponent(codeStr)}" title="Save Query">
              <i class="fa fa-save"></i>
            </button>
            <button class="btn btn-sm btn-success execute-sql" style="color: black;" data-code="${encodeURIComponent(codeStr)}" title="Execute Query">
              <i class="fa fa-play"></i>
            </button>
          </div>` : ''}
        </div>
      </div>
      <pre><code class="hljs language-${lang}">${highlighted}</code></pre>
    </div>${downloadBar(artifact)}`;
  }

  // No language specified, just return the code block
  return `<pre><code class="hljs">${highlighted}</code></pre>`;
};

marked.setOptions({ renderer });

export default {
  name: 'PAICE Chat',
  components: {
    McpAppRenderer,
    McpToolPermissionPrompt,
  },
  data() {
    return {
      currentChat: null,
      messages: [],
      userInput: '',
      isStreaming: false,
      // The in-progress assistant message. It lives inside `messages` so the same
      // McpAppRenderer instances are reused once streaming completes (no reload).
      liveMessage: null,
      // Hot streaming text buffer. Tokens are appended here (a cheap reactive
      // scalar) instead of onto liveMessage.content, so each token triggers a
      // light re-render and Vue does not coalesce many tokens into one flush.
      // Committed onto liveMessage.content when the stream finishes.
      streamingText: '',
      loadingResources: new Set(), // Track which resources are currently being fetched
      // The settings updatedAt this user has been shown, asserted on each send as
      // a conditional header so the service rejects (412) a message that would be
      // answered under settings changed since the chat was opened. Advanced only
      // when the user acknowledges the change, so dismissing the notice re-prompts
      // next send.
      //
      // Holds NO_SETTINGS_STORED ('*') rather than null when the settings read
      // came back with no stored record, so the send still asserts something.
      acknowledgedSettingsUpdatedAt: NO_SETTINGS_STORED,
      // Guards against stacking notices if a send is retried while one is open.
      settingsNoticeOpen: false,
      agentUrl: null,
      loadingChats: false,
      loadingMessages: false,
      mcpTools: [], // Cache of all MCP tools with metadata
      toolsWithUiMap: {}, // Map of tool name -> UI resource URI
      toolServerIdMap: {}, // Map of tool name -> serverId (GET /aquabot/tools)

      adminHistory: false,
      currentPage: 1,
      itemsPerPage: 10,
      chatLimit: 50,

      attachedFiles: [], // Array of { name, content } objects

      sqlClickHandler: null,
      fileTypeIcon: {
        'csv': 'fa-solid fa-file-csv',
        'sql': 'fa-solid fa-database',
        'py': 'fa-brands fa-python',
        'java': 'fa-brands fa-java',
        'js': 'fa-brands fa-square-js',
        'vue': 'fa-brands fa-vuejs',
        'ts': 'fa-brands fa-typescript',
        'go': 'fa-brands fa-golang',
      },

      aborting: false, // Flag to prevent multiple rapid aborts
      abortController: null,

      agentStatus: "", // Current (latest) agent status label, shown while streaming

      commentingMessageId: null,
      commentDraft: '',
      showScrollButton: false,
      animatingFeedback: {}, // e.g. { "msg123_thumbs_up": true } while the pop animation plays

      agentSettings: null,
    };
  },
  computed: {
    ...mapState({
      deleteChatRoles: state => state.paice.privilegedRoles,
      storedChatLimit: state => state.paice.chatLimit,
      storedAdminChatLimit: state => state.paice.adminChatLimit,
      storedChats: state => state.paice.chats,
      storedAdminChats: state => state.paice.adminChats,
    }),
    // The list for the scope currently selected. Both scopes stay cached in the
    // store, so toggling between them re-reads rather than refetches.
    chats() {
      return (this.adminHistory ? this.storedAdminChats : this.storedChats) || [];
    },
    currentClient() {
      return this.$store.state.currentClient;
    },
    accessToken() {
      return this.$store.state.accessToken;
    },
    totalPages() {
      return Math.ceil(this.chats.length / this.itemsPerPage);
    },
    paginatedChats() {
      const start = (this.currentPage - 1) * this.itemsPerPage;
      const end = start + this.itemsPerPage;
      return this.chats.slice(start, end);
    },
    startIndex() {
      return (this.currentPage - 1) * this.itemsPerPage;
    },
    endIndex() {
      return Math.min(this.startIndex + this.itemsPerPage, this.chats.length);
    },
    visiblePages() {
      const total = this.totalPages;
      const current = this.currentPage;
      
      if (total <= 5) {
        // Show all pages if 5 or fewer
        return Array.from({ length: total }, (_, i) => i + 1);
      }
      
      const pages = [];
      
      // Always show first page
      pages.push(1);
      
      // Determine if we need ellipsis on both sides
      const needsEllipsisBefore = current > 3;
      const needsEllipsisAfter = current < total - 2;
      
      if (needsEllipsisBefore && needsEllipsisAfter) {
        // Both ellipsis: show only current page in middle
        // Format: 1 ... current ... last (5 items total)
        pages.push('ellipsis');
        pages.push(current);
        pages.push('ellipsis');
        pages.push(total);
      } else if (needsEllipsisBefore) {
        // Only ellipsis before: show pages near end
        // Format: 1 ... last-2 last-1 last (5 items total)
        pages.push('ellipsis');
        pages.push(total - 2);
        pages.push(total - 1);
        pages.push(total);
      } else if (needsEllipsisAfter) {
        // Only ellipsis after: show pages near start
        // Format: 1 2 3 ... last (5 items total)
        pages.push(2);
        pages.push(3);
        pages.push('ellipsis');
        pages.push(total);
      } else {
        // No ellipsis needed (shouldn't happen with total > 5, but handle it)
        for (let i = 2; i <= total; i++) {
          pages.push(i);
        }
      }
      
      return pages;
    },
    getCurrentAgentStatus() {
      const steps = this.liveMessage?.statusUpdates?.length || 0;
      if (this.agentStatus) return `${this.agentStatus}...`;
      else if (this.isStreaming) return "Loading...";
      else return steps > 0 ? `Done - ${steps} steps` : "Done";
    },
    paiceSettings() {
      return this.$store.state.paice.paiceSettings;
    }
  },
  mounted() {
    this.initializeAgentUrl();
    // Get settings for displaying model id, and record the version this session
    // starts from so mid-session changes can be detected on send.
    this.getPaiceSettings()
      .then(() => { this.acknowledgedSettingsUpdatedAt = settingsVersionToken(this.paiceSettings); })
      .catch(() => {
        // The read failed, so we have no version to assert. Null sends neither
        // conditional header, skipping the staleness check — the right call here,
        // since the '*' default would instead claim settings were unconfigured
        // and 412 every send for a cid that actually has them.
        this.acknowledgedSettingsUpdatedAt = null;
      });
    this.chatLimit = this.storedChatLimit || 50;
    this.loadChats();
    this.setupAutoResize();
    this.loadMcpTools();
    
    // Store handler reference for cleanup
    this.sqlClickHandler = (event) => {
      const saveBtn = event.target.closest('.save-sql');
      const executeBtn = event.target.closest('.execute-sql');
      const downloadBtn = event.target.closest('.download-results');
      const groupHead = event.target.closest('.tool-calls-head');
      const toolRow = event.target.closest('.tool-row');

      if (groupHead) {
        const group = groupHead.closest('.tool-calls');
        this.setToolGroupOpen(group, !group.classList.contains('open'));
      } else if (toolRow) {
        const row = toolRow.closest('.tool-call');
        this.setToolRowOpen(row, !row.classList.contains('open'));
      } else if (saveBtn && !saveBtn.disabled) {
        const sql = decodeURIComponent(saveBtn.getAttribute('data-code'));
        this.handleSaveQuery(sql);
      } else if (executeBtn && !executeBtn.disabled) {
        const sql = decodeURIComponent(executeBtn.getAttribute('data-code'));
        executeBtn.disabled = true;
        this.handleExecuteQuery(sql);
      } else if (downloadBtn && !downloadBtn.disabled) {
        this.handleDownloadResults(
          downloadBtn.getAttribute('data-cid'),
          downloadBtn.getAttribute('data-qid'),
          downloadBtn,
        );
      }
    };
    document.addEventListener('click', this.sqlClickHandler);
  },
  beforeUnmount() {
    if (this.sqlClickHandler) {
      document.removeEventListener('click', this.sqlClickHandler);
    }
    releaseAgentStream(this);
  },
  watch: {
    isStreaming(streaming) {
      if (streaming) registerAgentStream(this, 'PAICE', () => this.abortStream());
      else releaseAgentStream(this);
    },
    chatLimit(v) {
      if (this.adminHistory) this.setAdminChatLimit(v);
      else this.setChatLimit(v);
    },
  },
  methods: {
    formatBytes,
    getAgentStatusIcon,
    currentStatus,
    ...mapActions({
      showModal: "showModal",
      hideModal: "hideModal",
      getPaiceSettings: "paice/getPaiceSettings",
      applyPaiceSettings: "paice/applyPaiceSettings",
      setChatLimit: "paice/setChatLimit",
      setAdminChatLimit: "paice/setAdminChatLimit",
      getChats: "paice/getChats",
      setChats: "paice/setChats",
    }),
    initializeAgentUrl() {
      // Set agent URL based on environment
      // const environment = this.$store.state.environment;
      // switch (environment) {
      //   case 'dev':
      //     this.agentUrl = 'https://ai-agent.api-dev.aqfer.net';
      //     break;
      //   case 'pre_prod':
      //     this.agentUrl = 'https://ai-agent.api-preprod.aqfer.net';
      //     break;
      //   case 'test':
      //     this.agentUrl = 'http://localhost:8080';
      //     break;
      //   default:
      //     this.agentUrl = 'https://ai-agent.api.aqfer.net';
      // }
      // this.agentUrl = 'https://lxlwuwtts3h4643r3k6zow3kg40flndd.lambda-url.us-west-2.on.aws'
      this.agentUrl = this.$store.state.agentsUrl
    },

    getAgentStatus(message) {
      if (message.isGuardrailIntervened == true) return "Blocked";
      const statusList = message.statusUpdates || [];
      const finishText = message.isResponseCancelled == true ? "Stopped" : "Done";
      return statusList?.length > 0 ? `${finishText} - ${statusList.length} steps` : finishText;
    },
    abortStream() {
      this.aborting = true;
      try {
        if (this.abortController) {
          this.abortController.abort();
          this.abortController = null;
        }

        // get latest user message
        const message = this.messages.slice().reverse().find(m => m.role === 'user');
        if (!message) {
          console.error('No latest user message found to abort stream...');
          throw new Error('No message found to abort stream. Please contact support');
        }

        // Set a cancellation flag to stop backend from execution (Lambda cannot detect client disconnect, so we need this workaround)
        axios.post(
          `${this.agentUrl}/v1/cids/${this.currentClient.cid}/aquabot/cancel`,
          {
            resourceId: message.resourceId,
            messageId: message.messageId,
          },
          {
            headers: {
              'Authorization': `Bearer ${this.accessToken}`,
            },
          }
        );
        this.$store.dispatch('addToast', { type: 'info', message: 'Generation stopped.' });
      } catch (error) {
        console.error('Failed to set cancellation flag to backend:', error);
        this.$store.dispatch('addToast', {
          type: 'error',
          message: 'Failed to stop generation. Please contact support.',
        });
      } finally {
        this.aborting = false;
      }
    },

    initSqlActionListeners() {
      // Use document-level delegation since messagesContainer 
      // may not exist yet (rendered conditionally with v-if)
      document.addEventListener('click', (event) => {
        const saveBtn = event.target.closest('.save-sql');
        const executeBtn = event.target.closest('.execute-sql');

        if (saveBtn && !saveBtn.disabled) {
          const sql = decodeURIComponent(saveBtn.getAttribute('data-code'));
          this.handleSaveQuery(sql);
        } else if (executeBtn && !executeBtn.disabled) {
          const sql = decodeURIComponent(executeBtn.getAttribute('data-code'));
          executeBtn.disabled = true;
          this.handleExecuteQuery(sql);
        }
      });
    },

    handleSaveQuery(sqlQuery) {
      this.showModal({
          component: "AddQuery",
          data: {
              query: {
                  Name: "",
                  Description: "",
                  QueryString: sqlQuery,
                  Internal: false,
                  Params: []
              }
          },
      });
    },

    async handleDownloadResults(cid, queryExecutionId, btn) {
      if (!cid || !queryExecutionId) {
        this.$store.dispatch('addToast', {
          type: 'error',
          message: 'Cannot download: this result is missing its query execution id.',
        });
        return;
      }

      // Minted on click, never stored: the link is presigned and expires in
      // minutes, so one captured at query time would be dead by now.
      const original = btn ? btn.innerHTML : null;
      if (btn) {
        btn.disabled = true;
        btn.innerHTML = '<i class="fa fa-spinner fa-spin me-1"></i> Preparing...';
      }

      try {
        const response = await axios.post(
          `${this.agentUrl}/v1/aquabot/tool`,
          {
            name: 'get_query_result_link',
            arguments: { cid, query_execution_id: queryExecutionId },
            cid,
            ...getAgentsPayloadParams(this.$store.state),
          },
          {
            headers: {
              'Authorization': `Bearer ${this.accessToken}`,
              'Content-Type': 'application/json',
            },
            timeout: 60000,
          }
        );

        // A 202 means an admin has set this tool to Ask under PAICE settings.
        // This button is a direct call with nowhere to show an approval card,
        // so it says what happened rather than failing as if the link were
        // missing. Asking for the download in the chat goes through the normal
        // prompt and works.
        if (response.status === 202) {
          throw new Error(
            'Downloading results needs approval for this client. Ask for the '
            + 'download in the chat and approve it there.'
          );
        }

        // MCP tools answer with a JSON string in a text content block, and
        // report tool-level failures in the body rather than by HTTP status.
        // `structuredContent` may itself wrap that same JSON string under
        // `result`, so unwrap one level before reading fields off it.
        const data = response.data || {};
        const asObject = (value) => {
          if (typeof value === 'string') {
            try {
              const parsed = JSON.parse(value);
              return parsed && typeof parsed === 'object' ? parsed : {};
            } catch (e) {
              return {};
            }
          }
          return value && typeof value === 'object' ? value : {};
        };

        let payload = asObject(data.structuredContent);
        if (payload.result !== undefined && payload.https === undefined) {
          payload = { ...payload, ...asObject(payload.result) };
        }
        if (!payload.https) {
          const text = (data.content || []).find(c => c.type === 'text')?.text;
          payload = { ...payload, ...asObject(text) };
        }
        if (data.isError || payload.error) {
          throw new Error(payload.error || 'Result link unavailable');
        }

        const url = payload.https;
        if (!url) {
          throw new Error('No download link was returned for this query');
        }

        // `download` only applies same-origin, so the filename is a hint the
        // signed S3 URL will ignore; `target` keeps the SPA from navigating away
        // if the object ever serves as inline content instead of a download.
        const link = document.createElement('a');
        link.href = url;
        link.download = `${queryExecutionId}.csv`;
        link.target = '_blank';
        link.rel = 'noopener noreferrer';
        document.body.appendChild(link);
        link.click();
        link.remove();
      } catch (error) {
        console.error('Failed to download query results:', error);
        this.$store.dispatch('addToast', {
          type: 'error',
          message: error.message?.includes('not yet ready')
            ? 'Results are not ready for download yet. Try again in a moment.'
            : 'Failed to download query results. Please try again.',
        });
      } finally {
        if (btn) {
          btn.disabled = false;
          if (original !== null) btn.innerHTML = original;
        }
      }
    },

    handleExecuteQuery(sqlQuery) {
      this.userInput = `Execute the following SQL query:\n\n\`\`\`sql\n${sqlQuery.trim()}\n\`\`\``;
      this.sendMessage();
    },

    setupAutoResize() {
      // Auto-resize textarea
      this.$nextTick(() => {
        const textarea = this.$refs.messageInput;
        if (textarea) {
          textarea.addEventListener('input', () => {
            textarea.style.height = 'auto';
            textarea.style.height = Math.min(textarea.scrollHeight, 200) + 'px';
          });
        }
      });
    },

    handleMessagesScroll() {
      const container = this.$refs.messagesContainer;
      if (!container) return;
      const distanceFromBottom = container.scrollHeight - container.scrollTop - container.clientHeight;
      this.showScrollButton = distanceFromBottom > 150;
    },

    async loadMcpTools() {
      // Fetch MCP tools with their UI metadata
      try {
        if (!this.agentUrl || !this.accessToken) {
          console.log('Cannot load MCP tools yet - missing agentUrl or accessToken');
          return;
        }

        const result = await fetchMcpTools(this.agentUrl, this.accessToken, this.$store.state, this.currentClient.cid);
        this.mcpTools = result.tools || [];

        // Sync with module cache to ensure UI mapping works in the renderer. (Eg, tool named "get_jobs" will be displayed as "MCP: get_jobs" in the UI)
        activeMcpTools = this.mcpTools;

        // Build map of tool name -> UI resource URI
        this.toolsWithUiMap = {};
        // ...and tool name -> the server it came from, so a UI is fetched from
        // (and calls tools on) its own server rather than the Aqfer default.
        this.toolServerIdMap = {};
        for (const tool of this.mcpTools) {
          const uiUri = getToolUiResourceUri(tool);
          if (uiUri) {
            this.toolsWithUiMap[tool.name] = uiUri;
            if (tool.serverId) this.toolServerIdMap[tool.name] = tool.serverId;
            console.log(`Tool "${tool.name}" has UI resource: ${uiUri}`);
          }
        }

        console.log(`Loaded ${this.mcpTools.length} MCP tools, ${Object.keys(this.toolsWithUiMap).length} with UI`);
      } catch (error) {
        console.warn('Failed to load MCP tools (non-fatal):', error);
        // Non-fatal error - continue without tool metadata
      }
    },

    // `forceGet` bypasses the store cache — used by the refresh button, the
    // limit input, and after a create/delete changes the list server-side.
    async loadChats(forceGet = false) {
      this.loadingChats = true;
      try {
        await this.getChats({
          adminHistory: this.adminHistory,
          limit: this.chatLimit,
          forceGet,
        });
        // Reset to first page when loading new chats
        this.currentPage = 1;
      } catch (error) {
        console.error('Failed to load chats:', error);
        this.$store.dispatch('addToast', {
          type: 'error',
          message: 'Failed to load chat history',
        });
      } finally {
        this.loadingChats = false;
      }
    },

    async createNewChat(skipSelect = false) {
      // If skipSelect is false (user clicked "New Chat" button), just clear the UI
      if (!skipSelect) {
        this.currentChat = null;
        this.messages = [];
        this.userInput = '';
        this.attachedFiles = [];
        this.showScrollButton = false;
        return;
      }

      // If skipSelect is true, we're creating from sendMessage, so create in DB
      try {
        const resourceId = `chat_${uuidv4()}`;
        const response = await axios.post(
          `${this.agentUrl}/v1/cids/${this.currentClient.cid}/aquabot/chats`,
          {
            resourceId,
            cid: this.currentClient.cid,
            ...getAgentsPayloadParams(this.$store.state),
          },
          {
            headers: {
              'Authorization': `Bearer ${this.accessToken}`,
            },
          }
        );

        // A chat the current user just created belongs in both scopes, so
        // prepend it to whichever caches have already been populated.
        if (this.storedChats != null) this.setChats({ chats: [response.data, ...this.storedChats] });
        if (this.storedAdminChats != null) this.setChats({ chats: [response.data, ...this.storedAdminChats], adminHistory: true });
        this.currentChat = response.data;

        return response.data;
      } catch (error) {
        console.error('Failed to create chat:', error);
        this.$store.dispatch('addToast', {
          type: 'error',
          message: 'Failed to create new chat',
        });
        throw error;
      }
    },

    async selectChat(chat) {
      this.currentChat = chat;
      this.messages = [];
      this.showScrollButton = false;
      await this.loadMessages();
    },

    async deleteChat(chat) {
      // Capture 'this' context for use in the confirm callback
      const self = this;
      
      // Use the Confirm modal instead of native confirm()
      this.showModal({
        component: "confirm",
        data: {
          title: "Delete Chat",
          message: `Are you sure you want to delete "${chat.title || 'New Chat'}"?`,
          confirmText: 'Delete',
          confirmClass: 'btn-danger',
          async confirm() {
            try {
              await axios.delete(
                `${self.agentUrl}/v1/cids/${self.currentClient.cid}/aquabot/chats/${chat.resourceId}`,
                {
                  headers: {
                    'Authorization': `Bearer ${self.accessToken}`,
                  },
                  params: getAgentsRequestQueryParams(self.$store.state),
                }
              );

              // Drop it from both cached scopes — the same chat can be present
              // in "Mine" and "All users", and only one of them is on screen.
              const without = list => (list || []).filter(c => c.resourceId !== chat.resourceId);
              if (self.storedChats != null) self.setChats({ chats: without(self.storedChats) });
              if (self.storedAdminChats != null) self.setChats({ chats: without(self.storedAdminChats), adminHistory: true });

              // If we deleted the current chat, clear it
              if (self.currentChat?.resourceId === chat.resourceId) {
                self.currentChat = null;
                self.messages = [];
              }

              self.$store.dispatch('addToast', {
                type: 'success',
                message: 'Chat deleted successfully',
              });
            } catch (error) {
              console.error('Failed to delete chat:', error);
              self.$store.dispatch('addToast', {
                type: 'error',
                message: 'Failed to delete chat',
              });
            }
          }
        }
      });
    },

    async loadMessages() {
      if (!this.currentChat) return;

      this.loadingMessages = true;
      try {
        const response = await axios.get(
          `${this.agentUrl}/v1/cids/${this.currentClient.cid}/aquabot/chats/${this.currentChat.resourceId}/messages`,
          {
            headers: {
              'Authorization': `Bearer ${this.accessToken}`,
            },
            params: getAgentsRequestQueryParams(this.$store.state),
          }
        );

        // Assign a stable clientId to each message so the v-for key (and the
        // McpAppRenderer key) stays constant for the life of the component.
        this.messages = (response.data || []).map(m => ({ ...m, clientId: m.clientId || `msg_${uuidv4()}` }));

        // Stop loading immediately so messages appear
        this.loadingMessages = false;

        // UI resources are stored with messages in database
        // McpAppRenderer handles rendering them via the uiResources array on each message

        this.scrollToBottom();
      } catch (error) {
        console.error('Failed to load messages:', error);
      } finally {
        this.loadingMessages = false;
      }
    },

    markResourceReady(resourceUri, messageIndex, resourceIndex) {
      // Mark resource as ready - McpAppRenderer handles fetching internally via props
      console.log(`Marking resource ${resourceUri} as ready for message ${messageIndex}, resource ${resourceIndex}`);

      if (this.messages[messageIndex] && this.messages[messageIndex].uiResources[resourceIndex]) {
        this.messages[messageIndex].uiResources[resourceIndex] = {
          uri: resourceUri,
          toolInput: null,
          toolResult: null,
          loading: false
        };
      }
    },

    async sendMessage() {
      const hasText = this.userInput.trim();
      const hasFiles = this.attachedFiles.length > 0;
      if (!hasText && !hasFiles) return;

      // Build the content: object format if files attached, string otherwise
      let messageContent;
      if (hasFiles) {
        messageContent = {
          input: this.userInput.trim(),
          files: [...this.attachedFiles],
        };
      } else {
        messageContent = this.userInput.trim();
      }

      const userMessage = {
        role: 'user',
        content: messageContent,
        timestamp: new Date().toISOString(),
        clientId: `msg_${uuidv4()}`,
      };
      // Add user message to UI
      this.messages.push(userMessage);
      this.userInput = '';
      this.attachedFiles = [];

      // Create a new chat if one doesn't exist
      if (!this.currentChat) {
        // Reset textarea height
        this.$nextTick(() => {
          const textarea = this.$refs.emptyMessageInput;
          if (textarea) {
            textarea.style.height = 'auto';
          }
        });

        // Create the chat without clearing messages
        await this.createNewChat(true);
      } else {
        // Reset textarea height
        this.$nextTick(() => {
          const textarea = this.$refs.messageInput;
          if (textarea) {
            textarea.style.height = 'auto';
          }
        });
      }

      this.scrollToBottom();

      // Save user message
      try {
        let response = await axios.post(
          `${this.agentUrl}/v1/cids/${this.currentClient.cid}/aquabot/chats/${this.currentChat.resourceId}/messages`,
          {
            messages: [userMessage],
            ...getAgentsPayloadParams(this.$store.state),
          },
          {
            headers: {
              'Authorization': `Bearer ${this.accessToken}`,
            },
          }
        );

        // Update user message with response content, for file attachments.
        // Preserve the existing clientId so the v-for key stays stable.
        console.log('Response data:', response.data);
        if (response?.data?.length > 0) {
          const existing = this.messages[this.messages.length - 1];
          this.messages[this.messages.length - 1] = { ...response.data[0], clientId: existing.clientId };
        }

        // Stream response
        await this.streamResponse();

        // Generate title if this is the first exchange (user + assistant)
        // After streaming, we should have exactly 2 messages
        console.log('Messages length after streaming:', this.messages.length);
        if (this.messages.length === 2) {
          console.log('Generating title for first message exchange');
          await this.generateTitle();
        } else {
          console.log('Skipping title generation, messages.length =', this.messages.length);
        }
      } catch (error) {
        console.error('Failed to send message:', error);
        this.$store.dispatch('addToast', {
          type: 'error',
          message: 'Failed to send message',
        });
      }
    },

    getFeedbackRating(message) {
      return message?.feedback?.rating || null;
    },

    async toggleRating(message, rating) {
      const current = this.getFeedbackRating(message);
      const newRating = current === rating ? null : rating;
      const prevFeedback = message.feedback ? { ...message.feedback } : null;
      const comment = prevFeedback?.comment || null;
      message.feedback = {
        rating: newRating,
        comment,
        updatedAt: new Date().toISOString(),
      };
      if (newRating) {
        this.pulseFeedback(message.messageId, newRating);
      }
      if (!newRating && this.commentingMessageId === message.messageId) {
        this.closeCommentBox();
      }
      try {
        await this.saveFeedback(message, { rating: newRating, comment });
      } catch (error) {
        message.feedback = prevFeedback;
        console.error('Failed to save feedback:', error);
        this.$store.dispatch('addToast', {
          type: 'error',
          message: 'Could not save feedback',
        });
      }
    },

    pulseFeedback(messageId, type) {
      const key = `${messageId}_${type}`;
      this.animatingFeedback = { ...this.animatingFeedback, [key]: true };
      setTimeout(() => {
        const updated = { ...this.animatingFeedback };
        delete updated[key];
        this.animatingFeedback = updated;
      }, 400); // matches the animation duration below
    },

    openCommentBox(message) {
      this.commentingMessageId = message.messageId;
      this.commentDraft = message.feedback?.comment || '';
      this.$nextTick(() => {
        const el = this.$refs.feedbackTextarea;
        const node = Array.isArray(el) ? el[0] : el;
        if (node && typeof node.focus === 'function') node.focus();
      });
    },

    closeCommentBox() {
      this.commentingMessageId = null;
      this.commentDraft = '';
    },

    async submitComment(message) {
      const rating = this.getFeedbackRating(message);
      const comment = this.commentDraft.trim() || null;
      const prevFeedback = message.feedback ? { ...message.feedback } : null;
      message.feedback = {
        rating,
        comment,
        updatedAt: new Date().toISOString(),
      };
      this.closeCommentBox();
      try {
        await this.saveFeedback(message, { rating, comment });
      } catch (error) {
        message.feedback = prevFeedback;
        console.error('Failed to save comment:', error);
        this.$store.dispatch('addToast', {
          type: 'error',
          message: 'Could not save comment',
        });
      }
    },

    async saveFeedback(message, payload) {
      await axios.post(
        `${this.agentUrl}/v1/aquabot/cids/${this.currentClient.cid}/chats/${this.currentChat.resourceId}/messages/${message.messageId}/feedback`,
        payload,
        {
          headers: { Authorization: `Bearer ${this.accessToken}` },
          params: getAgentsRequestQueryParams(this.$store.state),
        }
      );
    },

    buildSystemPrompt() {
      // Build custom system prompt with user information
      const userInfo = this.$store.state.user || {};
      const clientInfo = this.currentClient || {};

      let systemPrompt = `Today's date is ${new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}.

## Current User Context
- User: ${userInfo.email || 'Unknown'}
- Client: ${clientInfo.name || clientInfo.cid || 'Unknown'} (CID: ${clientInfo.cid || 'N/A'})
${userInfo.name ? `- Full Name: ${userInfo.name}` : ''}

`;

      const paiceFeature = (clientInfo.config?.features || []).find(f => f?.name === 'PAICE');
      if (paiceFeature?.chat_system_prompt) {
        systemPrompt += `${paiceFeature.chat_system_prompt}\n`;
      }

      return systemPrompt;
    },

    async streamResponse() {
      this.isStreaming = true;
      this.streamingText = ''; // Reset the hot streaming buffer
      this.detectedResourceUris = new Set(); // Track which resources we've already fetched

      // Create the in-progress assistant message and push it into `messages`.
      // We mutate this object in place as content/resources/status arrive, and on
      // completion we just flip `streaming` off and merge in the saved fields -
      // the DOM nodes (and the McpAppRenderer iframes) are never destroyed, so
      // there is no visible reload.
      // Snapshot the conversation to send to the backend BEFORE we add the empty
      // placeholder (the placeholder must not be part of the request payload).
      // Strip UI-only fields (clientId, streaming) from each message.
      const requestMessages = this.messages.map(({ clientId, streaming, ...m }) => m);

      const liveMessage = {
        role: 'assistant',
        content: '',
        statusUpdates: [],
        uiResources: [],
        queryArtifacts: [],
        // Approval prompts raised during this turn. Persisted with the message,
        // so reopening the chat still explains why a tool ran — or did not.
        toolPermissions: [],
        streaming: true,
        timestamp: new Date().toISOString(),
        clientId: `msg_${uuidv4()}`,
      };
      this.liveMessage = liveMessage;
      this.messages.push(liveMessage);
      this.scrollToBottom();

      // Track current tool call for UI detection
      let currentToolName = null;
      let currentToolInput = '';
      let inToolCodeBlock = false;
      this.abortController = new AbortController();
      this.agentStatus = null;
      // Set when the service rejects the send because settings moved. Acted on
      // after the try/finally so stream state is already torn down.
      let settingsConflict = false;

      try {
        const streamUrl = `${this.agentUrl}/v1/cids/${this.currentClient.cid}/aquabot`;
        const response = await fetch(streamUrl, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${this.accessToken}`,
            'Content-Type': 'application/json',
            // Assert the settings version this chat has been running under
            ...settingsPreconditionHeaders(this.acknowledgedSettingsUpdatedAt),
          },
          signal: this.abortController.signal,
          body: JSON.stringify({
            resourceId: this.currentChat.resourceId,
            messages: requestMessages,
            systemPrompt: this.buildSystemPrompt(),
            ...getAgentsPayloadParams(this.$store.state),
          }),
        });

        // Settings changed mid-session. Show what changed, and re-run the stream
        // from the top once the user accepts.
        if (response.status === 412) {
          this.$store.dispatch('addToast', {
            type: 'info',
            message: 'Conflict detected',
          });
          settingsConflict = true;
          return;
        }

        if (!response.ok) {
          throw new Error('Streaming failed');
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        // SSE frames are delimited by a blank line, and a network chunk can end
        // part-way through one. Whatever follows the last delimiter is held back
        // and prepended to the next chunk, so a frame that straddles a chunk
        // boundary is not silently dropped — which matters most for the largest
        // events, and a tool permission request carries the tool's full
        // arguments. `stream: true` does the same for a multi-byte character
        // split across the boundary.
        let buffer = '';
        let forever = true;
        while (forever) {
          const { done, value } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });
          console.log('Received chunk:', buffer);
          const lines = buffer.split('\n\n');
          // The tail is either an incomplete frame or the empty string left by a
          // chunk that ended cleanly on a delimiter; both are correct to carry.
          buffer = lines.pop();

          for (const line of lines) {
            if (!line.trim()) continue;

            console.log('Processing line:', line);

            const eventMatch = line.match(/^event: (.+)$/m);
            const dataMatch = line.match(/^data: (.+)$/m);

            if (eventMatch && dataMatch) {
              const eventType = eventMatch[1];
              const dataStr = dataMatch[1];

              console.log('Event type:', eventType, 'Data:', dataStr);

              if (eventType === 'text') {
                // Parse JSON data from SSE
                try {
                  const data = JSON.parse(dataStr);
                  console.log('Parsed text data:', data);
                  // Extract the text property from the JSON object
                  if (data.text) {
                    const text = data.text;
                    // Append to the hot buffer (cheap reactive scalar) so each
                    // token renders without coalescing. Committed to the message
                    // object only when streaming finishes.
                    this.streamingText += text;
                    // this.scrollToBottom();

                    // Detect tool code block start: ```tool_name, optionally
                    // suffixed with #toolUseId on query tools. Only the name is
                    // captured — the id is for the download control, not the map.
                    const toolStartMatch = text.match(/```(\w+)(?:#[\w-]+)?\n?$/);
                    if (toolStartMatch && !inToolCodeBlock) {
                      const toolName = toolStartMatch[1];
                      // Check if this tool has a UI resource
                      if (this.toolsWithUiMap[toolName]) {
                        console.log(`Tool with UI detected: ${toolName}`);
                        inToolCodeBlock = true;
                        currentToolName = toolName;
                        currentToolInput = '';
                      }
                    }
                    // Accumulate tool input while in code block
                    else if (inToolCodeBlock) {
                      // Check for code block end: ```
                      if (text.includes('```')) {
                        // Extract input before the closing ```
                        const endIdx = text.indexOf('```');
                        currentToolInput += text.substring(0, endIdx);

                        // Parse the tool input and create UI resource
                        try {
                          const toolInput = JSON.parse(currentToolInput.trim());
                          const uiUri = this.toolsWithUiMap[currentToolName];
                          console.log(`Creating UI resource for tool ${currentToolName} with URI ${uiUri}`);
                          this.addUiResource(uiUri, toolInput, null, currentToolName);
                        } catch (parseErr) {
                          console.warn('Could not parse tool input as JSON:', parseErr);
                          // Still create UI resource with raw input
                          const uiUri = this.toolsWithUiMap[currentToolName];
                          this.addUiResource(uiUri, { rawInput: currentToolInput.trim() }, null, currentToolName);
                        }

                        // Reset tool tracking state
                        inToolCodeBlock = false;
                        currentToolName = null;
                        currentToolInput = '';
                      } else {
                        // Still in code block, accumulate input
                        currentToolInput += text;
                      }
                    }
                  }
                } catch (e) {
                  console.error('Failed to parse text data:', e, dataStr);
                }
              } else if (eventType === 'tool_result' || eventType === 'tool' || eventType === 'function_result') {
                // Tool execution result received (support multiple event names)
                try {
                  const data = JSON.parse(dataStr);
                  console.log(`Received ${eventType} event:`, data);

                  // Extract tool name and result from various formats
                  const toolName = data.name || data.tool_name || data.function;
                  const result = data.result || data.output || data.content;

                  if (toolName && result !== undefined) {
                    // Format the result for the MCP App UI
                    const toolResult = {
                      content: result?.content || [{ type: 'text', text: JSON.stringify(result) }],
                      structuredContent: result?.structuredContent || result
                    };
                    this.updateUiResourceToolResult(toolName, toolResult);
                  }
                } catch (e) {
                  console.error(`Failed to parse ${eventType} data:`, e, dataStr);
                }
              } else if (eventType === 'done') {
                // Streaming complete - UI resources are now detected via tool metadata.
                // Finalize the live message in place (no re-render of the renderer).
                if (this.streamingText && this.streamingText.trim()) {
                  this.finalizeLiveMessage(liveMessage);
                } else {
                  console.warn('Streaming completed but no message content received');
                  // Nothing was generated - drop the empty placeholder.
                  this.removeLiveMessage(liveMessage);
                }
              } else if (eventType === 'error') {
                let errMsg = 'An error occurred while generating response. Please contact support'
                try {
                  const errorData = JSON.parse(dataStr);
                  console.error('Error details:', errorData);
                  if (errorData.message?.toLowerCase().includes("the provided model identifier is invalid")) {
                    errMsg = "The provided model identifier is invalid"
                  }
                } catch (e) {
                  // dataStr is not JSON, just log it
                  console.error('Stream error:', dataStr);
                }
                this.$store.dispatch('addToast', {
                  type: 'error',
                  message: errMsg,
                });
              } else if (eventType === 'artifact') {
                // A query tool finished and left a downloadable result behind.
                // Carries identifiers only — the link is minted on click.
                try {
                  const data = JSON.parse(dataStr);
                  if (data.type === 'query_result' && data.queryExecutionId) {
                    // Dedupe by execution id: when the agent runs a query and
                    // then visualizes the same results, only the first tool call
                    // gets the download button.
                    const seen = liveMessage.queryArtifacts.some(
                      a => a.queryExecutionId === data.queryExecutionId
                    );
                    if (!seen) {
                      liveMessage.queryArtifacts.push({
                        toolName: data.toolName,
                        toolUseId: data.toolUseId,
                        cid: data.cid,
                        queryExecutionId: data.queryExecutionId,
                      });
                    }
                  }
                } catch (e) {
                  console.error('Could not parse artifact event:', e, dataStr);
                }
              } else if (eventType === 'tool_permission_request') {
                // An external MCP tool needs the user's say-so. The stream is
                // now blocked on the answer, so the card has to render straight
                // away — it is pushed onto the live message rather than held in
                // component state so it survives the streaming -> saved
                // transition and gets persisted with the turn.
                try {
                  const data = JSON.parse(dataStr);
                  const already = liveMessage.toolPermissions.some(
                    p => p.toolUseId === data.toolUseId
                  );
                  if (!already) {
                    liveMessage.toolPermissions.push({ ...data, outcome: null });
                    this.scrollToBottom();
                  }
                } catch (e) {
                  console.error('Could not parse tool permission request:', e, dataStr);
                }
              } else if (eventType === 'tool_permission_resolved') {
                // How a prompt ended, including the ones nobody answered — a
                // timeout has to close its own card, or it sits there offering
                // buttons that no longer do anything.
                try {
                  const data = JSON.parse(dataStr);
                  const record = liveMessage.toolPermissions.find(
                    p => p.toolUseId === data.toolUseId
                  );
                  if (record) {
                    record.outcome = data.outcome;
                    if (data.decision) record.decision = data.decision;
                  }
                } catch (e) {
                  console.error('Could not parse tool permission resolution:', e, dataStr);
                }
              } else if (eventType === 'status') {
                // Optional: handle status updates if your backend sends them as events
                try {
                  const parsedData = JSON.parse(dataStr);
                  this.agentStatus = parsedData.label;
                  liveMessage.statusUpdates.push(parsedData);
                } catch (e) {
                  console.error('Could not parse status update:', e, dataStr);
                }
              } else if (eventType == 'blocked') {
                try {
                  const defaultReason = 'Sorry your input has been blocked by our guardrails.';
                  const data = JSON.parse(dataStr);
                  const reason = data.reason || defaultReason;
                  let toastoptions = {
                    message: defaultReason,
                    type: "error"
                  }
                  this.$store.dispatch('addToast', toastoptions);

                  // Turn the live message into the guardrail message, in place.
                  liveMessage.content = reason;
                  liveMessage.isGuardrailIntervened = true;
                  this.finalizeLiveMessage(liveMessage);
                } catch (e) {
                  console.error('Error in blocked chunk:', e);
                } finally {
                  forever = false; // stop reading further
                }
              }
            }
          }
        }
      } catch (error) {
        if (error.name === 'AbortError') {
          console.log('Stream aborted by user');
          // Save whatever partial content we already have
          if (this.streamingText?.trim()) {
            liveMessage.isResponseCancelled = true;
            this.finalizeLiveMessage(liveMessage);
          } else {
            // Nothing generated before abort - drop the empty placeholder.
            this.removeLiveMessage(liveMessage);
          }
        } else {
          console.error('Streaming error:', error);
          this.$store.dispatch('addToast', {
            type: 'error',
            message: 'Failed to stream response',
          });
          this.removeLiveMessage(liveMessage);
        }
      } finally {
        // If the stream ended without ever finalizing the placeholder (e.g. an
        // error event with no content), don't leave an empty assistant bubble.
        if (liveMessage.streaming) {
          if (this.streamingText?.trim()) {
            this.finalizeLiveMessage(liveMessage);
          } else {
            this.removeLiveMessage(liveMessage);
          }
        }
        this.isStreaming = false;
        this.abortController = null;
        this.agentStatus = null;
        this.liveMessage = null;
        this.scrollToBottom();

        // handling settings conflict
        if (settingsConflict) {
          await this.showSettingsChangedNotice();
        }
      }
    },

    /**
     * Settings changed since this chat started. Show what changed and, once the
     * user accepts, adopt the new settings and retry the send under them.
     *
     * The fetch below deliberately does NOT commit to the store: until the user
     * accepts, the settings in effect are still the ones this chat has been
     * running under. So dismissing the modal leaves the next send to conflict
     * again rather than quietly running under settings the user never saw.
     */
    async showSettingsChangedNotice() {
      if (this.settingsNoticeOpen) return;
      this.settingsNoticeOpen = true;

      const previous = this.paiceSettings || {};
      const previousPrompt = previous.systemPrompt || "";
      const previousModel = previous.modelId || "";

      let current = {};
      try {
        const response = await this.getPaiceSettings({ commit: false });
        current = response?.data || {};
      } catch (e) {
        this.settingsNoticeOpen = false;
        this.$store.dispatch('addToast', {
          type: 'error',
          message: 'Settings changed. Refresh the page to continue.',
        });
        return;
      }

      this.settingsNoticeOpen = false;
      this.showModal({
        component: 'PaiceSettingsConflict',
        data: {
          screensize: 'xl',
          details: {
            updated_by: current.updatedBy,
            updated_at: current.updatedAt,
          },
          fields: [
            {
              id: 'systemPrompt',
              label: 'System prompt',
              yours: previousPrompt,
              theirs: current.systemPrompt || "",
            },
            {
              id: 'modelId',
              label: 'Model',
              yours: previousModel,
              theirs: current.modelId || "",
            },
          ],
          onAcknowledge: async () => {
            // Only now do the new settings become the ones in effect.
            this.applyPaiceSettings(current);
            this.acknowledgedSettingsUpdatedAt = settingsVersionToken(current);
            this.hideModal();
            await this.streamResponse();
          },
        },
      });
    },

    // Flip the live (streaming) message into a completed one, in place, and
    // persist it. We deliberately keep the same object reference (and its
    // clientId / uiResources array) so the v-for key never changes and the
    // McpAppRenderer instances are reused rather than re-mounted.
    finalizeLiveMessage(message) {
      // Commit the streamed text onto the message BEFORE clearing the streaming
      // flag, so getMessageText switches from the buffer to message.content with
      // no empty frame. (The guardrail-block path sets content itself, so only
      // commit when content hasn't already been assigned.)
      if (!message.content) {
        message.content = this.streamingText;
      }
      message.streaming = false;

      // Build a clean payload for the backend (omit UI-only fields).
      const { clientId, streaming, ...payload } = message;
      this.createMessages([payload])
        .then(resp => {
          if (resp.data && resp.data.length > 0) {
            const savedMsg = resp.data[0];
            // Merge server-assigned fields (e.g. messageId) onto the existing
            // object. Keep our own clientId and the already-rendered uiResources /
            // statusUpdates array references so the v-for and renderer keys do not
            // change (the iframes must not re-mount).
            const {
              clientId, uiResources, statusUpdates, queryArtifacts, toolPermissions, ...serverFields
            } = savedMsg;
            // Mutate through the reactive array (not the raw closure reference) so Vue
            // actually detects the change and re-renders the feedback buttons.
            const idx = this.messages.indexOf(message);
            if (idx !== -1) {
              Object.assign(this.messages[idx], serverFields);
              this.messages[idx].streaming = false;
            }
          }
        })
        .catch(err => {
          console.error('Failed to save assistant message:', err);
        });
    },

    /**
     * The prompts still holding this turn up — the only ones that belong below
     * the bubble, because they are the only ones the user can still answer.
     *
     * A record with an outcome has been answered; a record on a message that is
     * no longer streaming has nowhere to send an answer, since the stream that
     * was polling for it is gone. Either way the card has nothing left to ask,
     * and what became of it is on the tool-call row it belongs to.
     */
    openToolPermissions(message) {
      if (!message.streaming) return [];
      return (message.toolPermissions || []).filter(p => !p.outcome);
    },

    /**
     * Answer an in-chat MCP tool permission prompt.
     *
     * The waiting stream polls for this write, so it is what unblocks the turn.
     * The optimistic outcome is applied only *after* the request lands: showing
     * "allowed" and then having the write fail would tell the user the tool ran
     * when it never will. The stream's own `tool_permission_resolved` confirms
     * it a moment later and is the authority — this is just so the buttons stop
     * offering a choice that has been made.
     */
    async onToolPermissionDecide({ record, decision }) {
      try {
        await this.$store.dispatch('paice/sendMcpToolDecision', {
          resourceId: this.currentChat.resourceId,
          toolUseId: record.toolUseId,
          decision,
          serverId: record.serverId,
          toolName: record.rawToolName,
        });
        record.decision = decision;
        record.outcome = decision === 'allow_once' ? 'allowed' : 'denied';
      } catch (e) {
        console.error('Failed to send tool permission decision:', e);
        // Rethrown so the card shows it and keeps its buttons live — the turn is
        // still blocked, and a swallowed failure would look like a hung chat.
        throw new Error(
          e?.response?.data?.detail || 'Could not send your answer. Try again.'
        );
      }
    },

    // Remove an in-progress placeholder message that produced no content.
    removeLiveMessage(message) {
      const idx = this.messages.indexOf(message);
      if (idx !== -1) this.messages.splice(idx, 1);
    },
    async createMessages(messages) {
      return axios.post(
        `${this.agentUrl}/v1/cids/${this.currentClient.cid}/aquabot/chats/${this.currentChat.resourceId}/messages`,
        {
          messages,
          ...getAgentsPayloadParams(this.$store.state),
        },
        {
          headers: {
            'Authorization': `Bearer ${this.accessToken}`,
          },
        }
      );
    },

    async generateTitle() {
      try {
        console.log('Generating title for chat:', this.currentChat.resourceId);
        const response = await axios.post(
          `${this.agentUrl}/v1/cids/${this.currentClient.cid}/aquabot/chats/${this.currentChat.resourceId}/title`,
          {
            messages: this.messages,
            ...getAgentsPayloadParams(this.$store.state),
          },
          {
            headers: {
              'Authorization': `Bearer ${this.accessToken}`,
            },
          }
        );

        console.log('Title generation response:', response.data);

        if (response.data.title) {
          this.currentChat.title = response.data.title;
          // Reflect the new title in the cached list for this scope
          const resourceId = this.currentChat.resourceId;
          this.setChats({
            chats: this.chats.map(c => c.resourceId === resourceId ? { ...c, title: response.data.title } : c),
            adminHistory: this.adminHistory,
          });
        } else {
          console.warn('No title returned from API');
        }
      } catch (error) {
        console.error('Failed to generate title:', error);
      }
    },

    isLastAssistantMessage(index) {
      // Check if this is the last assistant message in the array
      for (let i = this.messages.length - 1; i >= 0; i--) {
        if (this.messages[i].role === 'assistant') {
          return i === index;
        }
      }
      return false;
    },

    handleEnter(event) {
      if (!event.shiftKey) {
        event.preventDefault();
        this.sendMessage();
      }
      // Shift+Enter: let the default new-line behavior happen
    },

    getMessageText(message) {
      // While a message is streaming, read from the hot text buffer so live
      // tokens render immediately (and without re-touching the message object).
      if (message.streaming) {
        return this.streamingText;
      }
      let text = '';
      if (typeof message.content === 'string') {
        text = message.content;
      } else if (message.content && typeof message.content === 'object' && !Array.isArray(message.content)) {
        // { input, files } format
        text = message.content.input || '';
      } else if (Array.isArray(message.content)) {
        text = message.content
          .map(item => (item.type === 'text' ? item.text : ''))
          .join('');
      }

      return text.trim();
    },

    getMessageFiles(message) {
      if (message.content && typeof message.content === 'object' && (Array.isArray(message.content.files) || Array.isArray(message.content.attachments))) {
        return message.content.files || message.content.attachments;
      }
      return [];
    },

    renderMarkdown(text, message) {
      if (!text) return '';
      // Stage this message's download artifacts for renderer.code to claim.
      // Copied (not aliased) because the claim flag is per-render state — the
      // stored artifacts must stay clean for the next re-render.
      activeQueryArtifacts = (message?.queryArtifacts || []).map(
        a => ({ ...a, claimed: false })
      );
      // Same staging, same reason. Spreading each record also reads its
      // `decision` and `outcome`, which is what makes answering a prompt
      // re-render the body and land the chip on the row.
      activeToolPermissions = (message?.toolPermissions || []).map(
        p => ({ ...p, claimed: false })
      );
      // Read so the catalog landing re-renders every message: which fences are
      // tool calls depends on it, and a chat opened first would otherwise keep
      // showing them as code blocks until something else forced a redraw.
      void this.mcpTools.length;
      const messageKey = message?.clientId || message?.messageId || 'msg';
      return renderGroupedMarkdown(
        marked.lexer(text), messageKey, !!message?.streaming,
      );
    },

    /**
     * Open or close one tool row.
     *
     * The DOM is changed directly rather than through component state: a
     * message body is a v-html string, so a message that is not streaming is
     * never re-rendered and the row would never move. The key is also recorded
     * module-side, so a re-render mid-stream reopens what the user opened.
     */
    setToolRowOpen(row, open) {
      if (!row) return;
      row.classList.toggle('open', open);
      const toggle = row.querySelector('.tool-row');
      if (toggle) toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
      const key = row.getAttribute('data-tool-key');
      if (!key) return;
      if (open) openToolRows.add(key);
      else openToolRows.delete(key);
    },

    /** Show or hide a run's calls. Same reasoning as setToolRowOpen. */
    setToolGroupOpen(group, open) {
      if (!group) return;
      group.classList.toggle('open', open);
      const head = group.querySelector('.tool-calls-head');
      if (head) head.setAttribute('aria-expanded', open ? 'true' : 'false');
      const key = group.getAttribute('data-group-key');
      if (!key) return;
      if (open) openToolGroups.add(key);
      else openToolGroups.delete(key);
    },

    formatDate(dateString) {
      if (!dateString) return '';
      const date = new Date(dateString);
      const now = new Date();
      const diff = now - date;

      // Less than 1 minute
      if (diff < 60000) return 'Just now';

      // Less than 1 hour
      if (diff < 3600000) {
        const minutes = Math.floor(diff / 60000);
        return `${minutes}m ago`;
      }

      // Less than 1 day
      if (diff < 86400000) {
        const hours = Math.floor(diff / 3600000);
        return `${hours}h ago`;
      }

      // Less than 1 week
      if (diff < 604800000) {
        const days = Math.floor(diff / 86400000);
        return `${days}d ago`;
      }

      // Default format
      return date.toLocaleDateString();
    },

    scrollToBottom() {
      this.$nextTick(() => {
        const container = this.$refs.messagesContainer;
        if (container) {
          container.scrollTo({ top: container.scrollHeight, behavior: 'smooth' });
        }
      });
    },

    addUiResource(resourceUri, toolInput = null, toolResult = null, toolName = null) {
      // Add UI resource to streaming resources list
      console.log('Adding UI resource:', resourceUri, 'for tool:', toolName);

      const resource = {
        uri: resourceUri,
        toolInput,
        toolResult,
        toolName,
        loading: false
      };

      // Attach the resource to the in-progress live message so it renders inline
      // through the shared messages loop (same McpAppRenderer used after save).
      if (this.liveMessage) {
        this.liveMessage.uiResources.push(resource);
      }
      // Auto-scroll to bottom to show the newly added inline visualization
      this.scrollToBottom();
    },

    getVisualizationLabel(toolName) {
      // Return a user-friendly label for the visualization
      const labels = {
        'get_histogram_results': 'Histogram',
        'get_index_value_results': 'Index Values',
        'get-time': 'Server Time',
      };
      return labels[toolName] || 'Visualization';
    },

    updateUiResourceToolResult(toolName, toolResult) {
      // Update the tool result for a pending UI resource on the live message.
      console.log('Updating UI resource tool result for:', toolName);

      const resources = this.liveMessage?.uiResources || [];
      // Find the resource with matching tool name that doesn't have a result yet
      const resource = resources.find(r => r.toolName === toolName && !r.toolResult);

      if (resource) {
        // Mutate in place - Vue 3 reactivity picks this up, and the renderer's
        // deep watcher on `toolResult` forwards it to the (already mounted) iframe.
        resource.toolResult = toolResult;
        console.log('Updated tool result for resource:', resource.uri);
      } else {
        console.warn('No matching UI resource found for tool result:', toolName);
      }
    },

    triggerFileSelect() {
      this.$refs.fileInput.value = '';
      this.$refs.fileInput.click();
    },

    isBinaryFile(file) {
      const binaryExtensions = ['.avro', '.parquet'];
      const lower = file.name.toLowerCase();
      return binaryExtensions.some(ext => lower.endsWith(ext));
    },

    async handleFileSelect(event) {
      const files = Array.from(event.target.files);
      const MAX_TOTAL_SIZE = 5 * 1024 * 1024; // 5MB total limit

      // 1. Calculate size of files ALREADY attached
      const existingTotalSize = this.attachedFiles.reduce((acc, f) => acc + (f.size || 0), 0);

      // 2. Calculate size of the NEW batch
      const newBatchSize = files.reduce((acc, f) => acc + f.size, 0);

      // 3. Check if combined sum exceeds limit
      if (existingTotalSize + newBatchSize > MAX_TOTAL_SIZE) {
        const remainingSpace = formatBytes(MAX_TOTAL_SIZE - existingTotalSize);
        
        this.$store.dispatch('addToast', {
          type: 'error',
          message: `Total attachments exceed 5MB. You have ${remainingSpace} remaining.`,
        });
        
        // Clear input so the same files can be re-selected if curated
        event.target.value = ''; 
        return;
      }

      // 4. Process the files
      for (const file of files) {
        // Avoid duplicates by name
        if (this.attachedFiles.find(f => f.name === file.name)) continue;

        try {
          let content, encoding;
          if (this.isBinaryFile(file)) {
            content = await this.convertFileToBase64(file);
            encoding = 'base64';
          } else {
            content = await this.readFileAsText(file);
            encoding = 'text';
          }

          // Store the original file size so we can calculate the sum later
          this.attachedFiles.push({ 
            name: file.name, 
            content, 
            encoding, 
            size: file.size // Add this property
          });
        } catch (err) {
          this.$store.dispatch('addToast', {
            type: 'error',
            message: `Failed to read "${file.name}"`,
          });
        }
      }
    },

    readFileAsText(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (e) => resolve(e.target.result);
        reader.onerror = reject;
        reader.readAsText(file);
      });
    },

    convertFileToBase64(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          // reader.result looks like: "data:application/octet-stream;base64,AAAABBBBCCCC..."
          const base64 = reader.result.split(',')[1];
          resolve(base64); // returns only the Base64 part
        };
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
    },

    viewFileContent(rid, mid, file = null) {
      this.showModal({
        component: "AgentInputFileViewer",
        data: {
          title: 'PAICE - File Viewer',
          resourceId: rid, // chat id
          messageId: mid, // message id
          file: file,
          screensize: 'xl'
        }
      });
    },

    removeAttachedFile(index) {
      this.attachedFiles.splice(index, 1);
    },

    toggleAdminHistory() {
      this.adminHistory = !this.adminHistory;
      this.chatLimit = (this.adminHistory ? this.storedAdminChatLimit : this.storedChatLimit) || 50;
      this.currentPage = 1;

      // Cached per scope, so this only hits the network the first time each
      // scope is opened; later toggles read straight from the store.
      this.loadChats();
      this.currentChat = null;
      this.messages = [];
      this.showScrollButton = false;
    },
    isChatMine(chat) {
      // new chat
      if (!chat) return true;

      // check chat's user id matches current user's email
      return chat.userId === this.$store.state.currentUser.email;
    },
    goToPage(page) {
      if (page >= 1 && page <= this.totalPages) {
        this.currentPage = page;
        // Scroll to top of chat list when changing pages
        const chatList = document.querySelector('.chat-list');
        if (chatList) {
          chatList.scrollTop = 0;
        }
      }
    },
  },
};
</script>

<style scoped>
.ai-assistant-container {
  height: 100vh;
  background: #E7EBF2;
}

.row {
  height: 100%;
  margin: 0;
  display: flex;
  flex-wrap: nowrap;
}

/* Sidebar */
.chat-sidebar {
  background: white;
  border-right: 1px solid #dee2e6;
  padding: 0;
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
  flex: 0 0 25%;
  width: 25%;
  min-width: 17rem;
}

.sidebar-header {
  padding: 1.25rem 1rem;
  border-bottom: 2px solid #dee2e6;
  background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
  display: flex;
  gap: 4px;
  justify-content: space-between;
  align-items: center;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.sidebar-header h3 {
  margin: 0;
  font-weight: 700;
  color: #12263F;
  font-size: 1.5rem;
  letter-spacing: -0.02em;
}

/* Chat history scope toggle (Mine / All users) */
.history-scope-toggle {
  display: inline-flex;
  align-items: center;
  padding: 2px;
  background: #eef1f5;
  border-radius: 999px;
  gap: 2px;
}

.scope-option {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  border: none;
  background: transparent;
  color: #6c757d;
  font-size: 0.75rem;
  font-weight: 600;
  line-height: 1;
  padding: 0.375rem 0.65rem;
  border-radius: 999px;
  cursor: pointer;
  transition: background-color 0.15s ease, color 0.15s ease, box-shadow 0.15s ease;
}

.scope-option i {
  font-size: 0.7rem;
}

.scope-option:hover:not(.active) {
  color: #12263F;
}

.scope-option.active {
  background: #ffffff;
  color: #12263F;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.12);
}

/* When "All users" is active, the second segment reads as an explicit admin
   mode rather than a generic selected pill. */
.history-scope-toggle .scope-option:last-child.active {
  background: #f9a826;
  color: #402c00;
}

/* A thin top accent on the whole sidebar reinforces admin scope even when the
   toggle itself has scrolled out of view. */
.chat-sidebar.admin-view-active {
  box-shadow: inset 0 3px 0 0 #f9a826;
}

.admin-view-banner {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  font-size: 0.75rem;
  font-weight: 600;
  color: #7a5200;
  background: #fef3e0;
  border-bottom: 1px solid #f3dfb0;
}

.admin-view-banner i {
  font-size: 0.8rem;
  color: #f9a826;
}

/* Chat list controls (count + fetch limit + refresh) */
.sidebar-list-controls {
  padding: 0.5rem 0.75rem;
  border-bottom: 1px solid #e9edf2;
  background: #f8f9fb;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  flex-shrink: 0;
}

.chat-count-pill {
  display: inline-flex;
  align-items: baseline;
  gap: 0.3rem;
  font-size: 0.7rem;
  color: #6c757d;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  white-space: nowrap;
  min-width: 0;
}

.chat-count-pill i {
  font-size: 0.75rem;
  color: #9aa3af;
}

.chat-count-pill strong {
  font-size: 0.85rem;
  font-weight: 700;
  color: #12263F;
}

.chat-count-pill > span {
  overflow: hidden;
  text-overflow: ellipsis;
}

.list-controls-actions {
  display: flex;
  align-items: center;
  gap: 0.35rem;
  flex-shrink: 0;
}

/* The limit input reads as one unit with its "Show" label rather than a bare
   number field, so its purpose is legible without the tooltip. */
.limit-field {
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  margin: 0;
  padding: 0.15rem 0.4rem 0.15rem 0.5rem;
  background: #ffffff;
  border: 1px solid #dde2e9;
  border-radius: 0.375rem;
  cursor: pointer;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}

.limit-field:focus-within {
  border-color: #0d6efd;
  box-shadow: 0 0 0 2px rgba(13, 110, 253, 0.15);
}

.limit-field-label {
  font-size: 0.65rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #9aa3af;
}

.limit-field-input {
  width: 2rem;
  border: none;
  background: transparent;
  color: #12263F;
  font-size: 0.78rem;
  font-weight: 600;
  text-align: center;
  padding: 0;
  outline: none;
  -moz-appearance: textfield;
  appearance: textfield;
}

.limit-field-input::-webkit-outer-spin-button,
.limit-field-input::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

.list-control-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.58rem;
  height: 1.58rem;
  border: 1px solid #dde2e9;
  border-radius: 0.375rem;
  background: #ffffff;
  color: #6c757d;
  font-size: 0.75rem;
  cursor: pointer;
  transition: color 0.15s ease, border-color 0.15s ease, background-color 0.15s ease;
}

.list-control-btn:hover:not(:disabled) {
  color: #0d6efd;
  border-color: #0d6efd;
  background: #f0f6ff;
}

.list-control-btn:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.chat-list {
  overflow-y: auto;
  flex: 1;
  /* Without this a long list refuses to shrink, pushing the pagination footer
     past the bottom of the sidebar instead of scrolling internally. */
  min-height: 0;
}

.chat-item {
  padding: 1rem;
  border-bottom: 1px solid #f0f0f0;
  cursor: pointer;
  transition: background-color 0.2s;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
}

.chat-item:hover {
  background-color: #f8f9fa;
}

.chat-item.active {
  background-color: #e7f3ff;
  border-left: 3px solid #0d6efd;
}

.chat-item.active.otherUserChat {
  background-color: #f8f9fa;
  border-left: 3px solid #6c757d; /* Grey border for other users' chats */
}

.chat-info {
  flex: 1;
  min-width: 0;
}

.chat-title {
  font-weight: 500;
  margin-bottom: 0.25rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.chat-date {
  font-size: 0.75rem;
  color: #6c757d;
}

.chat-user {
  font-size: 0.75rem;
  margin-top: 0rem;
}

.delete-chat-btn {
  background: none;
  border: none;
  color: #6c757d;
  cursor: pointer;
  padding: 0.25rem 0.5rem;
  border-radius: 0.25rem;
  transition: all 0.2s;
  opacity: 0;
  flex-shrink: 0;
}

.chat-item:hover .delete-chat-btn {
  opacity: 1;
}

.delete-chat-btn:hover {
  background: #f8d7da;
  color: #dc3545;
}

.no-chats {
  padding: 2rem 1rem;
  text-align: center;
  color: #6c757d;
}

.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem 1rem;
  color: #6c757d;
  gap: 1rem;
}

.loading-container p {
  margin: 0;
  font-size: 0.9rem;
}

/* Chat Area */
.chat-area {
  padding: 0;
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
  /* Takes whatever the sidebar leaves.
     min-width: 0 lets it shrink below its content instead of overflowing. */
  flex: 1 1 auto;
  min-width: 0;
  position: relative;
}

.empty-state-container {
  display: flex;
  flex-direction: column;
  justify-content: center;
  height: 100%;
  padding: 1rem 1rem;
}

.empty-state-content {
  text-align: center;
  color: #6c757d;
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
}

.empty-state-content i {
  color: #dee2e6;
}

.paice-logo {
  width: 150px;
  height: 150px;
  object-fit: contain;
  margin-bottom: 1.5rem;
  border-radius: 0.5rem;
}

.empty-input-area {
  border-top: 1px solid #dee2e6;
}

.chat-container {
  display: flex;
  flex-direction: column;
  height: 100%;
  position: relative;
}

/* Messages */
.messages-container {
  flex: 1;
  overflow-y: auto;
  padding: 2rem 1rem;
}

.message {
  display: flex;
  margin-bottom: 1.5rem;
  animation: fadeIn 0.3s;
  width: 100%;
  max-width: 100%;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.message-avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  margin-right: 1rem;
  overflow: hidden;
}

.message.user .message-avatar {
  background: #0d6efd;
  color: white;
}

.message.assistant .message-avatar {
  background: #ffffff;
  border: 1px solid #dee2e6;
}

.avatar-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.message-content {
  flex: 1;
  min-width: 0;
  max-width: 100%;
}

.message-feedback {
  position: relative;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 0.15rem;
  margin-top: 0.2rem;
  opacity: 0;
  transition: opacity 0.15s ease;
}

.message.assistant:hover .message-feedback,
.message-feedback.has-feedback {
  opacity: 1;
}

.feedback-btn {
  border: none;
  background: transparent;
  color: #6e84a3;
  font-size: 0.85rem;
  padding: 0.2rem 0.4rem;
  border-radius: 4px;
  cursor: pointer;
  line-height: 1;
  transition: color 0.15s ease;
}

.feedback-btn i {
  display: inline-block;
  transition: transform 0.1s ease;
}

.feedback-btn:active:not(:disabled) i {
  transform: scale(0.8);
}

.feedback-btn:disabled {
  cursor: not-allowed;
}

.feedback-button-thumbs-up:not(.disabled):hover, .feedback-button-thumbs-up.active:not(.disabled) {
  color: #2c7be5;
}

.feedback-button-thumbs-down:not(.disabled):hover, .feedback-button-thumbs-down.active:not(.disabled) {
  color: #2c7be5;
}

.feedback-button-comment:not(.disabled):hover, .feedback-button-comment.active:not(.disabled) {
  color: #2c7be5;
}


.feedback-label {
  font-size: 0.75rem;
  color: #6e84a3;
  margin-right: 0.35rem;
  margin-top: 0.05rem;
  white-space: nowrap;
}

@keyframes feedback-pop {
  0%   { transform: scale(1); }
  40%  { transform: scale(1.45) rotate(-6deg); }
  70%  { transform: scale(0.9); }
  100% { transform: scale(1); }
}

.feedback-btn.just-rated i {
  animation: feedback-pop 0.4s ease;
}

.feedback-popover {
  position: absolute;
  right: 0;
  bottom: calc(100% + 0.4rem);
  z-index: 30;
  width: 280px;
  background: #fff;
  border: 1px solid #dee2e6;
  border-radius: 6px;
  padding: 0.6rem;
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.08);
}

.message-text {
  background: #f8f9fa;
  padding: 0.75rem 1rem;
  border-radius: 0.5rem;
  line-height: 1.6;
  word-wrap: break-word;
  overflow-wrap: break-word;
}

.message.user .message-text {
  background: #e7f3ff;
}

.message-text>>>p:last-child {
  margin-bottom: 0;
}

/* ── Code blocks ─────────────────────────────────────────────────────────────
   highlight.js ships one theme process-wide — github-dark, imported at the top
   of this file, over vs2015 from libs.scss — so every block came out near-black
   in both modes. In a #f8f9fa bubble that reads as a hole punched in the
   message, not as code inside it. So the chat states its own surface for each
   mode, and its own token palette for light, where github-dark's colours were
   picked against black and are barely legible.

   All of it is deep-scoped to this component's .message-text: `pre`, `code` and
   `.hljs` are used elsewhere in the portal — TalkToMyData.vue renders the same
   markup — and those pages keep the global theme untouched. */
.message-text>>>pre {
  background: #eef2f8;
  border: 1px solid #dde4ee;
  padding: 1rem;
  border-radius: 0.5rem;
  overflow-x: auto;
  margin: 0.75rem 0;
  max-width: 100%;
  width: 100%;
  box-sizing: border-box;
}

.message-text>>>pre code {
  background: transparent;
  padding: 0;
  color: #12263f;
  font-family: 'Courier New', Consolas, Monaco, monospace;
  font-size: 0.875rem;
  line-height: 1.5;
}

.message-text>>>code {
  background: transparent;
  padding: 0.2rem 0.4rem;
  border-radius: 0.25rem;
  font-family: 'Courier New', Consolas, Monaco, monospace;
  font-size: 0.875rem;
}

.message-text>>>.hljs {
  background: transparent;
  color: #12263f;
}

/* Light-mode token palette: GitHub Light, on Dashkit's ink for plain text.
   Prefixed with `body:not(.dark-mode)` rather than restated twice — in dark mode
   these selectors simply do not match and the imported github-dark theme, which
   is already right for a dark block, comes through untouched. */
body:not(.dark-mode) .message-text>>>.hljs-comment,
body:not(.dark-mode) .message-text>>>.hljs-code,
body:not(.dark-mode) .message-text>>>.hljs-formula {
  color: #6a737d;
}

body:not(.dark-mode) .message-text>>>.hljs-keyword,
body:not(.dark-mode) .message-text>>>.hljs-doctag,
body:not(.dark-mode) .message-text>>>.hljs-type,
body:not(.dark-mode) .message-text>>>.hljs-template-tag,
body:not(.dark-mode) .message-text>>>.hljs-template-variable,
body:not(.dark-mode) .message-text>>>.hljs-meta .hljs-keyword,
body:not(.dark-mode) .message-text>>>.hljs-variable.language_ {
  color: #d73a49;
}

body:not(.dark-mode) .message-text>>>.hljs-title,
body:not(.dark-mode) .message-text>>>.hljs-title.class_,
body:not(.dark-mode) .message-text>>>.hljs-title.function_ {
  color: #6f42c1;
}

body:not(.dark-mode) .message-text>>>.hljs-attr,
body:not(.dark-mode) .message-text>>>.hljs-attribute,
body:not(.dark-mode) .message-text>>>.hljs-literal,
body:not(.dark-mode) .message-text>>>.hljs-meta,
body:not(.dark-mode) .message-text>>>.hljs-number,
body:not(.dark-mode) .message-text>>>.hljs-operator,
body:not(.dark-mode) .message-text>>>.hljs-variable,
body:not(.dark-mode) .message-text>>>.hljs-selector-attr,
body:not(.dark-mode) .message-text>>>.hljs-selector-class,
body:not(.dark-mode) .message-text>>>.hljs-selector-id,
body:not(.dark-mode) .message-text>>>.hljs-section {
  color: #005cc5;
}

body:not(.dark-mode) .message-text>>>.hljs-string,
body:not(.dark-mode) .message-text>>>.hljs-regexp,
body:not(.dark-mode) .message-text>>>.hljs-meta .hljs-string {
  color: #032f62;
}

body:not(.dark-mode) .message-text>>>.hljs-built_in,
body:not(.dark-mode) .message-text>>>.hljs-symbol {
  color: #b34700;
}

body:not(.dark-mode) .message-text>>>.hljs-name,
body:not(.dark-mode) .message-text>>>.hljs-quote,
body:not(.dark-mode) .message-text>>>.hljs-selector-tag,
body:not(.dark-mode) .message-text>>>.hljs-selector-pseudo {
  color: #22863a;
}

body:not(.dark-mode) .message-text>>>.hljs-bullet {
  color: #735c0f;
}

body:not(.dark-mode) .message-text>>>.hljs-subst,
body:not(.dark-mode) .message-text>>>.hljs-emphasis,
body:not(.dark-mode) .message-text>>>.hljs-strong {
  color: #12263f;
}

/* Diffs carry their own wash, so these two set a background as well. */
body:not(.dark-mode) .message-text>>>.hljs-addition {
  color: #22863a;
  background-color: #e6ffed;
}

body:not(.dark-mode) .message-text>>>.hljs-deletion {
  color: #b31d28;
  background-color: #ffeef0;
}

/* ── Code blocks, dark ───────────────────────────────────────────────────────
   Recessed against the #282c34 bubble and drawn from the same dark palette as
   the rest of the page, instead of the theme's #0d1117 — a block darker than
   the app's own body background reads as a cut-out. Literal hex because this
   block is plain CSS and cannot see _user-variables: card #1a1d23, border
   #2e3238, ink #e1e3e8. Kept here, deep-scoped, rather than in the global
   body.dark-mode block at the foot of this file, which would also repaint
   TalkToMyData.vue. */
body.dark-mode .message-text>>>pre {
  background: #1a1d23;
  border-color: #2e3238;
}

body.dark-mode .message-text>>>pre code,
body.dark-mode .message-text>>>.hljs {
  color: #e1e3e8;
}

body.dark-mode .message-text>>>.code-block-wrapper {
  border-color: #2e3238;
}

/* Beats the `#464647 !important` in the global dark block below, which still
   applies to the other pages that render this markup. */
body.dark-mode .message-text>>>.code-block-header {
  background: #20242b !important;
  border-bottom-color: #2e3238;
}

body.dark-mode .message-text>>>.code-block-lang {
  color: #a0a4b8;
}

/* Table styles */
.message-text>>>table {
  width: 100%;
  border-collapse: collapse;
  margin: 1rem 0;
  background: white;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  border-radius: 0.5rem;
  overflow: hidden;
}

.message-text>>>thead {
  background: linear-gradient(135deg, #8a9cad 0%, #7a8c9d 100%);
  color: white;
}

.message-text>>>thead th {
  padding: 0.625rem 1rem;
  text-align: left;
  font-weight: 600;
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  border-bottom: 2px solid #7a8c9d;
}

.message-text>>>tbody tr {
  border-bottom: 1px solid #e9ecef;
  transition: background-color 0.2s;
}

.message-text>>>tbody tr:last-child {
  border-bottom: none;
}

.message-text>>>tbody tr:hover {
  background-color: #f8f9fa;
}

.message-text>>>tbody td {
  padding: 0.75rem 1rem;
  font-size: 0.875rem;
  color: #495057;
  vertical-align: top;
}

.message-text>>>tbody tr:nth-child(even) {
  background-color: #fafbfc;
}

.message-text>>>tbody tr:nth-child(even):hover {
  background-color: #f1f3f5;
}

/* Code block wrapper with header for tool names */
.message-text>>>.code-block-wrapper {
  margin: 0.75rem 0;
  border: 1px solid #dde4ee;
  border-radius: 0.5rem;
  overflow: hidden;
  max-width: 100%;
  width: 100%;
  box-sizing: border-box;
}

.message-text>>>.code-block-header {
  background: #e2e8f1;
  padding: 0.5rem 1rem;
  border-bottom: 1px solid #dde4ee;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.message-text>>>.download-results-bar {
  display: flex;
  justify-content: flex-end;
  margin: -0.4rem 0 0.75rem;
}

.message-text>>>.download-results {
  padding: 0;
  font-size: 0.75rem;
  line-height: 1.2;
  text-decoration: none;
}

.message-text>>>.download-results:hover:not(:disabled) {
  text-decoration: underline;
}

.message-text>>>.code-block-lang {
  color: #4c5f7a;
  font-size: 0.75rem;
  font-family: 'Courier New', Consolas, Monaco, monospace;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

/* Inside a wrapper the header and the block are one card, so the block drops
   its own frame and corners rather than drawing a second border under the
   header. */
.message-text>>>.code-block-wrapper pre {
  margin: 0;
  border: 0;
  border-radius: 0;
}

/* ── Tool calls ───────────────────────────────────────────────────────────────
   A run of tool calls collapses to one line: how many, and which. They are how
   the answer was reached rather than the answer itself, and a turn that polls a
   long-running query makes a dozen of them — so nothing opens on its own. Two
   levels, both a click: the header shows the calls, a call shows its input. */
.message-text>>>.tool-calls {
  position: relative;
  margin: 0.75rem 0;
  border: 1px solid #edf2f9;
  border-radius: 0.5rem;
  background: #f9fbfd;
  overflow: hidden;
}

/* The run's state in the page's own colour language: amber while a call is in
   flight, quiet grey once the calls have been made. Nothing claims success —
   the fence carries a tool's input, not its result. */
.message-text>>>.tool-calls::before {
  content: '';
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 2px;
  background: #d2ddec;
}

.message-text>>>.tool-calls.running::before {
  background: #d9822b;
  animation: tool-rail-pulse 1.4s ease-in-out infinite;
}

@keyframes tool-rail-pulse {

  0%,
  100% {
    opacity: 1;
  }

  50% {
    opacity: 0.3;
  }
}

.message-text>>>.tool-calls-head {
  display: flex;
  align-items: baseline;
  gap: 0.5rem;
  width: 100%;
  min-width: 0;
  padding: 0.45rem 0.7rem 0.45rem 0.85rem;
  border: 0;
  background: #f1f4f9;
  text-align: left;
}

.message-text>>>.tool-calls-head:focus-visible,
.message-text>>>.tool-row:focus-visible {
  outline: 2px solid #2c7be5;
  outline-offset: -2px;
}

.message-text>>>.tool-calls-count {
  flex-shrink: 0;
  font-size: 0.72rem;
  font-weight: 600;
  color: #12263f;
}

/* Which tools ran, so the closed line is worth reading on its own. */
.message-text>>>.tool-calls-names,
.message-text>>>.tool-calls-active {
  flex: 1;
  min-width: 0;
  font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  font-size: 0.7rem;
  color: #95aac9;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.message-text>>>.tool-calls-active {
  color: #b56a1d;
}

.message-text>>>.tool-calls-body,
.message-text>>>.tool-drawer {
  /* 0fr -> 1fr animates to the content's own height without measuring it. */
  display: grid;
  grid-template-rows: 0fr;
  transition: grid-template-rows 0.16s ease-out;
}

.message-text>>>.tool-calls.open>.tool-calls-body,
.message-text>>>.tool-call.open .tool-drawer {
  grid-template-rows: 1fr;
}

/* A grid item defaults to min-width: auto, so it sizes to its widest line
   rather than to the strip — which let the input block outgrow the group and
   get clipped by its overflow. Zero here is what lets `pre` scroll instead. */
.message-text>>>.tool-calls-body-inner,
.message-text>>>.tool-drawer-inner {
  overflow: hidden;
  min-width: 0;
  min-height: 0;
}

.message-text>>>.tool-call {
  border-top: 1px solid #edf2f9;
}

.message-text>>>.tool-row {
  display: flex;
  align-items: baseline;
  gap: 0.5rem;
  width: 100%;
  min-width: 0;
  padding: 0.4rem 0.7rem 0.4rem 1.15rem;
  border: 0;
  background: none;
  text-align: left;
}

.message-text>>>.tool-chevron {
  flex-shrink: 0;
  font-size: 0.6rem;
  color: #b1c2d9;
  transition: transform 0.16s ease-out;
}

.message-text>>>.tool-calls.open>.tool-calls-head>.tool-chevron,
.message-text>>>.tool-call.open>.tool-row>.tool-chevron {
  transform: rotate(90deg);
}

/* Left as the server names it. Uppercasing snake_case makes it unreadable, and
   the strip's own shape already says this was a tool and not a code sample. */
.message-text>>>.tool-name {
  flex-shrink: 0;
  font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  font-size: 0.75rem;
  font-weight: 600;
  color: #12263f;
}

.message-text>>>.tool-precis {
  flex: 1;
  min-width: 0;
  font-size: 0.72rem;
  color: #95aac9;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.message-text>>>.tool-flag {
  flex-shrink: 0;
  font-size: 0.65rem;
  color: #2c7be5;
}

/* Owns the spacing around the pending cards. It has to be a flex container, not
   a bare block: as a block it has no padding or border of its own, so the cards'
   margins collapse through it and then collapse again with the `mb-2` on the
   status row above — 12px against 8px reads as a 4px change. Flex suppresses
   collapsing entirely, and `gap` is the only thing that separates two stacked
   cards, which sibling margins would have collapsed into one. */
.tool-perm-list {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  margin-top: 0.75rem;
}

/* What the user answered for this call. Reads as a state marker, not a control:
   the answer has already been given, and the row's own drawer is the thing
   that opens. Same allow / deny colours as the card and the settings page. */
.message-text>>>.tool-perm-chip {
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  flex-shrink: 0;
  padding: 0.1rem 0.4rem;
  border-radius: 0.75rem;
  font-size: 0.65rem;
  font-weight: 600;
  white-space: nowrap;
}

.message-text>>>.tool-perm-chip i {
  font-size: 0.6rem;
}

.message-text>>>.tool-perm-chip-allowed {
  background: rgba(0, 168, 84, 0.1);
  color: #00864a;
}

.message-text>>>.tool-perm-chip-denied {
  background: rgba(230, 55, 87, 0.1);
  color: #d02545;
}

.message-text>>>.tool-perm-chip-stale {
  background: rgba(149, 170, 201, 0.16);
  color: #6e84a3;
}

.message-text>>>.tool-row,
.message-text>>>.tool-call,
.message-text>>>.tool-drawer {
  min-width: 0;
}

.message-text>>>.tool-drawer pre {
  width: auto;
  margin: 0 0.7rem 0.6rem 1.15rem;
  padding: 0.7rem 0.8rem;
  border-radius: 0.375rem;
  max-height: 16rem;
  overflow: auto;
}

.message-text>>>.tool-drawer pre code {
  font-size: 0.72rem;
  line-height: 1.5;
}

.message-text>>>.tool-drawer .download-results-bar {
  justify-content: flex-start;
  margin: 0 1.15rem 0.6rem;
}

@media (max-width: 575.98px) {

  /* The arguments drop to their own line rather than being squeezed into an
     unreadable sliver beside the name. */
  .message-text>>>.tool-row {
    flex-wrap: wrap;
  }

  .message-text>>>.tool-precis {
    flex-basis: 100%;
    padding-left: 1.1rem;
  }
}

@media (prefers-reduced-motion: reduce) {

  .message-text>>>.tool-chevron,
  .message-text>>>.tool-calls-body,
  .message-text>>>.tool-drawer {
    transition: none;
  }

  .message-text>>>.tool-calls.running::before {
    animation: none;
  }
}

.blocked-message-text {
  background: #fdf2f2;
  color: #5c1b1b;
  border: 1.2px solid #dc3545;
  font-style: italic;
}
.slight-margin-down {
    margin-bottom: 0.4px;
}

.streaming-indicator {
  display: flex;
  gap: 0.25rem;
  margin-top: 0.5rem;
}

.streaming-indicator .dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #6c757d;
  animation: pulse 1.5s infinite;
}

.streaming-indicator .dot:nth-child(2) {
  animation-delay: 0.2s;
}

.streaming-indicator .dot:nth-child(3) {
  animation-delay: 0.4s;
}

@keyframes pulse {

  0%,
  60%,
  100% {
    opacity: 0.3;
    transform: scale(0.8);
  }

  30% {
    opacity: 1;
    transform: scale(1);
  }
}

/* UI Resources */
.ui-resources {
  margin-top: 0.75rem;
}

.ui-resource-container {
  margin-bottom: 0.75rem;
  border-radius: 0.5rem;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  background: white;
}

.ui-resource-container:last-child {
  margin-bottom: 0;
}

/* Ensure ui-resource-renderer takes full height */
.ui-resource-container ui-resource-renderer {
  display: block;
  width: 100%;
  height: 100%;
}

/* Target shadow DOM content inside ui-resource-renderer */
.ui-resource-container ui-resource-renderer::part(container) {
  height: 100%;
}

.ui-resource-container>>>ui-resource-renderer>*,
.ui-resource-container ::v-deep ui-resource-renderer>* {
  height: 100%;
}

.ui-resource-container>>>ui-resource-renderer iframe,
.ui-resource-container ::v-deep ui-resource-renderer iframe {
  height: 100%;
  width: 100%;
  border: none;
}

.resource-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem 1rem;
  background: white;
  color: #6c757d;
  gap: 1rem;
}

.resource-loading p {
  margin: 0;
  font-size: 0.9rem;
}

/* UI Resource Buttons */
.ui-resource-buttons {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-top: 0.75rem;
}

.ui-resource-btn {
  display: inline-flex;
  align-items: center;
  font-size: 0.85rem;
  padding: 0.4rem 0.75rem;
  border-radius: 20px;
  transition: all 0.2s ease;
}

.ui-resource-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
}

.ui-resource-btn.active {
  background-color: #0d6efd;
  color: white;
  border-color: #0d6efd;
}

/* Visualization Flyout Panel */
.visualization-flyout {
  position: fixed;
  top: 0;
  right: 0;
  width: 50%;
  max-width: 800px;
  min-width: 400px;
  height: 100vh;
  background: white;
  box-shadow: -4px 0 20px rgba(0, 0, 0, 0.15);
  z-index: 1050;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.flyout-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 1.25rem;
  border-bottom: 1px solid #dee2e6;
  background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
}

.flyout-title {
  margin: 0;
  font-size: 1.1rem;
  font-weight: 600;
  color: #333;
  display: flex;
  align-items: center;
}

.btn-close-flyout {
  background: none;
  border: none;
  font-size: 1.25rem;
  color: #6c757d;
  cursor: pointer;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  transition: all 0.2s ease;
}

.btn-close-flyout:hover {
  background: #f0f0f0;
  color: #333;
}

.flyout-content {
  flex: 1;
  overflow: auto;
  padding: 0;
}

.flyout-content .mcp-app-container {
  height: 100%;
  min-height: 100%;
}

/* Flyout backdrop */
.flyout-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.3);
  z-index: 1040;
}

/* Flyout transitions */
.flyout-enter-active,
.flyout-leave-active {
  transition: transform 0.3s ease;
}

.flyout-enter-from,
.flyout-leave-to {
  transform: translateX(100%);
}

/* Responsive: full width on smaller screens */
@media (max-width: 992px) {
  .visualization-flyout {
    width: 100%;
    max-width: none;
    min-width: auto;
  }
}

/* Input Area */
.input-area {
  border-top: 1px solid #dee2e6;
  padding: 1rem;
  padding-bottom: 0.5rem;;
  background: white;
}

.input-wrapper {
  border: 1.5px solid #dee2e6;
  border-radius: 0.75rem;
  background: white;
  transition: border-color 0.2s, box-shadow 0.2s;
  overflow: hidden;
}

.input-wrapper:focus-within {
  border-color: #86b7fe;
  box-shadow: 0 0 0 3px rgba(13, 110, 253, 0.1);
}

/* File attachments bar */
.file-attachments-bar {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  padding: 0.6rem 0.75rem 0;
}

.file-chip {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  /* background: #eaf1ff; */
  background: #e7f3ff;
  border: 1px solid #2C7BE5;
  border-radius: 20px;
  padding: 0.25rem 0.5rem 0.25rem 0.6rem;
  font-size: 0.8rem;
  color: #2C7BE5;
  max-width: 200px;
}

.file-chip-icon {
  font-size: 0.75rem;
  flex-shrink: 0;
}

.file-chip-name {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 130px;
}

.file-chip-remove {
  background: none;
  border: none;
  color: #6c757d;
  cursor: pointer;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  flex-shrink: 0;
  font-size: 0.7rem;
  transition: background 0.15s, color 0.15s;
}

.file-chip-remove:hover {
  background: rgba(255, 0, 21, 0.1);
  color: #dc3545;
}

/* Textarea inside wrapper */
.input-textarea {
  border: none !important;
  border-radius: 0 !important;
  box-shadow: none !important;
  resize: none;
  max-height: 200px;
  min-height: 44px;
  padding: 0.75rem;
  background: transparent !important;
}

.input-textarea:focus {
  outline: none;
  box-shadow: none !important;
}

/* Toolbar row */
.input-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.4rem 0.6rem 0.4rem 0.5rem;
  border-top: 1px solid #f0f0f0;
  background: #fafafa;
}

.toolbar-actions {
  display: flex;
  align-items: center;
  gap: 0.25rem;
}

.toolbar-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  color: #6c757d;
  background: #f0f0f0;
  border: 1px solid #dee2e6;
  border-radius: 0.5rem;
  padding: 0.3rem 0.6rem;
  font-size: 0.8rem;
  transition: all 0.15s;
}

.toolbar-btn:hover:not(:disabled) {
  background: #e9e9e9;
  border-color: #d4d4d4;
  color: #333;
}

.toolbar-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.model-id {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  color: #0088ff;
  background: #e7f3ff;
  border: 1px solid #c1d5ea;
  border-radius: 0.5rem;
  padding: 0.3rem 0.6rem;
  font-size: 0.8rem;
}

.input-hint {
  font-size: 0.72rem;
  white-space: nowrap;
}

.ai-disclaimer {
  font-size: 0.72rem;
  color: #6c757d;
  text-align: center;
  padding: 0.5rem 0.75rem 0;
}

.send-button {
  height: 36px;
  width: 36px;
  padding: 0;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  font-size: 0.85rem;
}

/* Attached files shown in sent user messages */
.message-files {
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
  margin-bottom: 0.4rem;
}

.message-file-chip {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  background: #e7f3ff;
  border: 1px solid #2C7BE5;
  border-radius: 20px;
  padding: 0.2rem 0.65rem;
  font-size: 0.78rem;
  color: #2C7BE5;
  max-width: 220px;
  cursor: pointer;
  transition: background 0.15s, border-color 0.15s;
}

.message-file-chip:hover {
  background: #d2e8ff;
  border-color: #2C7BE5;
}

.message-file-chip:active {
  background: #b6cfff;
}

.message-file-chip-view-all {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  background: var(--bs-primary);
  border-radius: 20px;
  padding: 0.2rem 0.65rem;
  font-size: 0.78rem;
  color: #ffffff;
  cursor: pointer;
}

.message-file-chip-view-all:hover {
  background: #116ce3;
}

.message-file-name {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 160px;
}

/* Scrollbar */
.messages-container::-webkit-scrollbar,
.chat-list::-webkit-scrollbar {
  width: 6px;
}

.messages-container::-webkit-scrollbar-track,
.chat-list::-webkit-scrollbar-track {
  background: #f1f1f1;
}

.messages-container::-webkit-scrollbar-thumb,
.chat-list::-webkit-scrollbar-thumb {
  background: #888;
  border-radius: 3px;
}

.messages-container::-webkit-scrollbar-thumb:hover,
.chat-list::-webkit-scrollbar-thumb:hover {
  background: #555;
}

.chat-pagination {
  padding: 0.75rem 1rem;
  border-top: 1px solid #e9edf2;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  background: white;
  /* Pinned: the sidebar is a flex column and only .chat-list scrolls, so this
     footer must never be squeezed or scrolled out of view. */
  flex-shrink: 0;
}

.pagination-info {
  font-size: 0.75rem;
  color: #6c757d;
}

.chat-pagination .pagination {
  margin: 0;
}

.chat-pagination .page-link {
  color: #6c757d;
  border-color: #dee2e6;
  padding: 0.375rem 0.75rem;
}

.chat-pagination .page-link:hover {
  color: #0d6efd;
  background-color: #e9ecef;
  border-color: #dee2e6;
}

.chat-pagination .page-item.active .page-link {
  background-color: #0d6efd !important;
  border-color: #0d6efd !important;
  color: white !important;
}

.chat-pagination .page-item.disabled .page-link {
  color: #6c757d;
  pointer-events: none;
  cursor: auto;
  background-color: #fff;
  border-color: #dee2e6;
  opacity: 0.5;
}

.message-text >>> .execute-sql:disabled,
.message-text >>> .save-sql:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  pointer-events: none;
}

.message.user .message-text >>> .code-block-header {
  display: none !important;
}

.btn-link:hover {
  text-decoration: underline;
}

/* ── Agent status accordion ──────────────────────────────────────────────── */
.agent-status {
  display: flex;
  align-items: center;
  gap: 6.5px;
  cursor: pointer;
  user-select: none;
}

.status-text { font-size: 13px; }

/* FIX: all saved-message panels get same scroll cap via attribute selector */
[id^="collapse-"] {
  max-height: 300px;
  overflow-y: auto;
}

.status-list { list-style: none; padding: 0; margin: 0; }

.status-row {
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding-top: 0.45rem;
  padding-bottom: 0.375rem;
}

.status-row:last-child { border-bottom: 0 !important; }

/* FIX: arrow visibility driven by Bootstrap's .collapsed class on the trigger */
.agent-status.collapsed .collapse-arrow-up { display: none !important; }
.agent-status:not(.collapsed) .collapse-arrow-down { display: none !important; }

/* Inline UI Resource Cards */
.ui-resource-card {
  border: 1px solid #dee2e6;
  border-radius: 0.5rem;
  overflow: hidden;
  margin-bottom: 1rem;
  background-color: white;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

.ui-resource-header {
  background: #f8f9fa;
  padding: 0.6rem 1rem;
  border-bottom: 1px solid #dee2e6;
  font-weight: 600;
  color: #333;
  display: flex;
  align-items: center;
  font-size: 0.9rem;
}

/* Scroll to bottom button */
.scroll-to-bottom-btn {
  position: absolute;
  bottom: 150px;
  left: 50%;
  transform: translateX(-50%);
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  background: white;
  color: #0d6efd;
  border: 1.5px solid #0d6efd;
  border-radius: 20px;
  padding: 0.35rem 0.85rem;
  font-size: 0.8rem;
  font-weight: 500;
  cursor: pointer;
  box-shadow: 0 2px 10px rgba(13, 110, 253, 0.2);
  transition: all 0.15s ease;
  z-index: 10;
  white-space: nowrap;
}

.scroll-to-bottom-btn:hover {
  background: #0d6efd;
  color: white;
  box-shadow: 0 4px 14px rgba(13, 110, 253, 0.35);
}

.scroll-btn-fade-enter-active,
.scroll-btn-fade-leave-active {
  transition: opacity 0.2s ease, transform 0.2s ease;
}

.scroll-btn-fade-enter-from,
.scroll-btn-fade-leave-to {
  opacity: 0;
  transform: translateX(-50%) translateY(6px);
}
</style>

<style lang="scss">
@use '@/theme/scss/_user-variables.scss' as *;
body.dark-mode {

  /* ── Tool calls ───────────────────────────────────────────────────────────
     Same strip, same status language. The drawer's own surface is set with the
     rest of the code blocks in the scoped block above, so only the collapsed
     rows need restating here. */
  .tool-calls {
    background: $dark-body-bg !important;
    border-color: $dark-border-color !important;
  }

  .tool-call {
    border-color: $dark-border-color !important;
  }

  .tool-calls-names,
  .tool-precis {
    color: $dark-text-muted !important;
  }

  .tool-calls-active {
    color: #e0a458 !important;
  }

  .tool-calls-count,
  .tool-name {
    color: $dark-text-primary !important;
  }

  .tool-chevron {
    color: #5c6675 !important;
  }

  .tool-calls-head {
    background: $dark-card-bg !important;
  }

  .tool-calls::before {
    background: #4a5361 !important;
  }

  .tool-calls.running::before {
    background: #e0a458 !important;
  }

  .tool-calls {
    background: $dark-elevated-bg !important;
  }

  .tool-flag {
    color: #7ab0ff !important;
  }

  /* Chip tones lifted to the dark-surface variants the permission card and
     McpServers.vue already use, so the three views agree on the shade. The
     fill is what carries the state at this size; the text only has to stay
     legible on it. */
  .tool-perm-chip-allowed {
    background: rgba(79, 209, 138, 0.16) !important;
    color: #4fd18a !important;
  }

  .tool-perm-chip-denied {
    background: rgba(240, 138, 156, 0.16) !important;
    color: #f08a9c !important;
  }

  .tool-perm-chip-stale {
    background: rgba(160, 164, 184, 0.16) !important;
    color: $dark-text-secondary !important;
  }

  .chat-pagination {
    color: #a0a4b8 !important;
    border-color: #2e3238 !important;
    background-color: #1a1d23 !important;
  }

  .empty-state-container {
    // background-color: #1a1d23 !important;
    background-color: #111317 !important;
  }

  .chat-item {
        &:hover {
            background-color: $dark-hover-bg !important;
            color: $dark-text-primary !important;
        }
        &.active {
            background-color: $dark-selected-bg !important;
            color: $dark-text-primary !important;
        }
    }

    .chat-item {
        border-bottom-color: $dark-border-color !important;
    }

    .input-area {
        border-top-color: $dark-border-color !important;
        background-color: $dark-card-bg !important;
    }

    .empty-input-area {
        border-top-color: $dark-border-color !important;
        background-color: #111317 !important;
    }

    .chat-container {
        background-color: $dark-card-bg !important;
    }

    .messages-container {
        background-color: #111317 !important;
    }

    .message-text {
        background-color: $dark-elevated-bg !important;
    }

    .message.user .message-text {
      background: rgb(42, 58, 80) !important
    }

    .input-wrapper {
      border-color: $dark-border-color !important;
      background-color: $dark-elevated-bg !important;
    }

    .input-wrapper:focus-within {
      border-color: #4d7cfe !important;
      box-shadow: 0 0 0 3px rgba(77, 124, 254, 0.15) !important;
    }

    .input-textarea {
      background-color: transparent !important;
      color: $dark-text-primary !important;
    }

    .input-toolbar {
      background-color: $dark-card-bg !important;
      border-top-color: $dark-border-color !important;
    }

    .toolbar-btn {
      color: #a0a4b8 !important;
      background: $dark-input-bg !important;
      border-color: $dark-border-color !important;
    }

    .toolbar-btn:hover:not(:disabled) {
      background: $dark-hover-bg !important;
      border-color: $dark-border-color !important;
      color: $dark-text-primary !important;
    }

    .model-id {
      color: $dark-accent !important;
      background: rgba(0, 128, 255, 0.1) !important;
      border-color: #14488b !important;
    }

    .file-chip {
      background: $dark-body-bg !important;
      border-color: $dark-accent !important;
      color: $dark-accent !important;
    }

    .message-file-chip {
      background: $dark-body-bg !important;
      border-color: $dark-accent !important;
      color: $dark-accent !important;
    }

    .message-file-chip:hover {
      background: rgb(23, 39, 63) !important;
    }

    .message-file-chip-view-all {
      color: $dark-body-bg !important;
    }

    .message-text table {
        background-color: $dark-elevated-bg !important;
    }

    .message-text thead th {
        background: #4e5051 !important;
        border: none !important;
        border-right: 1px solid #2e3238 !important;
        color: white !important;
    }

    .message-text tbody tr:nth-child(even) {
        background-color: $dark-elevated-bg !important;
    }
    
    .message-text tbody tr:nth-child(even):hover {
        background-color: $dark-hover-bg !important;
    }

    .message-text tbody td {
        background-color: $dark-elevated-bg !important;
        color: $dark-text-primary !important;
    }

    .message-text tbody tr {
        border-bottom: 1px solid #4e5051 !important;
    }
    .message-text tbody tr:last-child {
        border-bottom: none !important;
    }

    .message-text .code-block-header {
        background: #464647 !important;
    }

    .blocked-message-text {
      background: rgba(220, 53, 69, 0.1) !important; /* Subtle dark red tint */
      color: #ffc4c4 !important; /* Soft, readable pink-white */
      border: 1.2px solid #dc3545 !important;
    }

    .delete-chat-btn:hover {
      background: #ff00151c;
      color: #dc3545;
    }

    .feedback-popover {
      background: $dark-card-bg;
      border: 1px solid $dark-border-color;
      box-shadow: 0 4px 14px rgba(0, 0, 0, 0.08);
    }

    .ui-resource-card {
      border-color: $dark-border-color !important;
      background-color: $dark-card-bg !important;
    }

    .ui-resource-header {
      background-color: $dark-elevated-bg !important;
      border-bottom-color: $dark-border-color !important;
      color: $dark-text-primary !important;
    }
    
    .scroll-to-bottom-btn {
      background: $dark-card-bg;
      color: $dark-accent;
      border-color: $dark-accent;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.4);

      &:hover {
        background: $dark-accent;
        color: $dark-body-bg;
        box-shadow: 0 4px 12px rgba(77, 124, 254, 0.3);
      }
    }

    .feedback-label {
      color: #a0a4b8 !important;
    }

    .history-scope-toggle {
      background: $dark-input-bg !important;
    }

    .scope-option {
      color: #a0a4b8 !important;
    }

    .scope-option:hover:not(.active) {
      color: $dark-text-primary !important;
    }

    .scope-option.active {
      background: $dark-border-color !important;
      color: $dark-text-primary !important;
      box-shadow: 0 1px 3px rgba(0, 0, 0, 0.4) !important;
    }

    .history-scope-toggle .scope-option:last-child.active {
      background: #f9a826 !important;
      color: #241700 !important;
    }

    .admin-view-banner {
      background: rgba(249, 168, 38, 0.12) !important;
      border-bottom-color: rgba(249, 168, 38, 0.3) !important;
      color: #f5c76a !important;
    }

    .sidebar-list-controls {
      background: $dark-input-bg !important;
      border-bottom-color: $dark-border-color !important;
    }

    .chat-count-pill {
      color: #a0a4b8 !important;
    }

    .chat-count-pill i {
      color: #7b8290 !important;
    }

    .chat-count-pill strong {
      color: $dark-text-primary !important;
    }

    .limit-field {
      background: $dark-elevated-bg !important;
      border-color: $dark-border-color !important;
    }

    .limit-field-label {
      color: #7b8290 !important;
    }

    .limit-field-input {
      color: $dark-text-primary !important;
    }

    .list-control-btn {
      background: $dark-elevated-bg !important;
      border-color: $dark-border-color !important;
      color: #a0a4b8 !important;
    }

    .list-control-btn:hover:not(:disabled) {
      color: #4d7cfe !important;
      border-color: #4d7cfe !important;
      background: rgba(77, 124, 254, 0.12) !important;
    }

}
</style>